<template lang="pug">
.bq-agent(ref="agentRoot")
    .bq-meta
        .bq-section-title
            .bq-title-row
                .bq-title-left
                    .bq-ai-select.bq-ai-select--platform(
                        v-if="!initialGateLoading"
                        @click.stop=""
                    )
                        button.bq-ai-select-btn(
                            type="button"
                            :disabled="sending || typing || uploadingAttachments || initialGateLoading || clearingHistory || savingPlatform"
                            :class="{ open: platformMenuOpen }"
                            @click.prevent="togglePlatformMenu"
                        )
                            span.bq-ai-select-value
                                img.bq-ai-logo(v-if="platformLogo" :src="platformLogo" :alt="platformLabel")
                                span {{ agentTitle }}
                            span.bq-ai-caret ▾
                        select.bq-ai-select-native(
                            v-if="isMobile"
                            :value="selectedAiPlatform"
                            :disabled="savingPlatform"
                            aria-label="AI Platform"
                            @change="onPlatformSelectFromNative"
                        )
                            option(v-for="p in platformOptions" :key="p.id" :value="p.id") {{ p.label }}
                        .bq-ai-options.bq-platform-options(v-if="!isMobile && platformMenuOpen")
                            button.bq-ai-option(
                                v-for="p in platformOptions"
                                :key="p.id"
                                type="button"
                                :class="{ active: selectedAiPlatform === p.id }"
                                @click="onPlatformSelect(p.id)"
                                :data-track="`agent_platform_${p.id}`"
                            )
                                img.bq-ai-logo(v-if="p.logo" :src="p.logo" :alt="p.label")
                                span {{ p.label }}
                .bq-title-right
                    .bq-ai-select.bq-ai-select--inline(
                        v-if="!initialGateLoading"
                        @click.stop=""
                    )
                        button.bq-ai-select-btn(
                            type="button"
                            :disabled="!chatEnabled || fetchingModels || sending || typing || uploadingAttachments || initialGateLoading || clearingHistory || savingModel"
                            :class="{ open: modelMenuOpen }"
                            @click.prevent="toggleModelMenu"
                        )
                            span.bq-ai-select-value
                                span(v-if="!chatEnabled") No Model
                                span(v-else-if="fetchingModels || savingModel") Loading...
                                span(v-else-if="!availableModels.length") No models
                                span(v-else) {{ effectiveModelLabel }}
                            span.bq-ai-caret ▾
                        select.bq-ai-select-native(
                            v-if="isMobile && availableModels.length"
                            :value="effectiveModel"
                            :disabled="fetchingModels || savingModel"
                            aria-label="AI Model"
                            @change="onModelSelectFromNative"
                        )
                            option(v-for="m in availableModels" :key="m.id" :value="m.id") {{ m.display_name || m.id }}
                        .bq-ai-options.bq-model-options(v-if="!isMobile && modelMenuOpen")
                            button.bq-ai-option(
                                v-for="m in availableModels"
                                :key="m.id"
                                type="button"
                                :class="{ active: effectiveModel === m.id }"
                                @click="onModelSelect(m.id)"
                                :data-track="`agent_model_${m.id}`"
                            )
                                span {{ m.display_name || m.id }}
                    span.material-symbols-outlined.bq-icon.bq-icon--danger(
                        v-if="chatEnabled && messages.length > 0"
                        :class="{ disabled: sending || typing || uploadingAttachments || initialGateLoading || clearingHistory }"
                        title="Clear chat history"
                        aria-label="Clear chat history"
                        role="button"
                        tabindex="0"
                        @click="onClearHistoryClick"
                        @keydown.enter.prevent="onClearHistoryClick"
                        @keydown.space.prevent="onClearHistoryClick"
                        data-track="agent_clearHistory"
                    ) delete_history

        .bq-chat(
            ref="chatBox"
            :class="{ 'drag-here': dragHere }"
            @dragenter.stop.prevent="onChatDragEnter"
            @dragover.stop.prevent="onChatDragOver"
            @dragleave.stop.prevent="onChatDragLeave"
            @drop.stop.prevent="onChatDrop"
        )
            .bq-drop-overlay(v-if="dragHere")
                .bq-drop-overlay-inner
                    svg(width="32" height="32")
                        use(xlink:href="/material-icon.svg#icon-upload")
                    span Drop files or folders to attach
            .bq-apikey-overlay(v-if="!initialGateLoading && !hasAiPlatform")
                .bq-apikey-overlay-inner
                    p.bq-apikey-help Select an AI platform to start chatting.
                    .bq-platform-overlay-options
                        button.btn.bq-platform-overlay-btn(
                            v-for="p in platformOptions"
                            :key="p.id"
                            type="button"
                            :disabled="savingPlatform"
                            @click="onPlatformSelect(p.id)"
                            :data-track="`agent_platformOverlay_${p.id}`"
                        )
                            img.bq-apikey-logo(v-if="p.logo" :src="p.logo" :alt="p.label" :class="`bq-apikey-logo--${p.id}`")
                            span {{ p.label }}
            .bq-apikey-overlay(v-if="!initialGateLoading && hasAiPlatform && (!hasAiApiKey || apiKeyInvalid)")
                .bq-apikey-overlay-inner
                    img.bq-apikey-logo(v-if="platformLogo" :src="platformLogo" :alt="platformLabel")
                    p.bq-apikey-error(v-if="hasAiApiKey && apiKeyInvalid") Your API key is invalid
                    button.btn(type="button" :disabled="savingApiKey || loadingApiKey" :class="{ 'is-loading-key': loadingApiKey }" @click="openApiKeyModal" data-track="agent_openApiKey")
                        span(v-if="loadingApiKey") {{ apiKeyLoadingDots }}
                        span(v-else) Enter API Key
                    p.bq-apikey-help
                        span.wordset Get your API key:&nbsp;
                        a.bq-link(:href="apiKeyDocsUrl" target="_blank" rel="noopener noreferrer") {{ apiKeyDocsUrl }}
            .bq-messages(ref="messagesBox" @scroll.passive="onHistoryScroll")
                .bq-gate-loading(v-if="initialGateLoading")
                    BunnyLoader(
                        :overlay="false"
                        :label="savingPlatform ? 'Switching...' : chatEnabled ? 'Fetching history...' : 'Loading...'"
                    )

                template(v-else)
                    .bq-history-loading(v-if="showHistoryLoadingIndicator")
                        span {{ historyLoadingLabel }}

                    .bq-message.is-assistant.bq-empty-greeting(v-if="!messages.length && chatEnabled")
                        .bq-bubble
                            | Hi! Ask me anything about your project
                            template(v-if="projectName") &nbsp;
                                strong "{{ projectName }}"
                            | . You can also add data to the project by attaching files or pasting plain text into the chat.
                    .bq-message(
                        v-for="(msg, idx) in messages"
                        :key="idx"
                        :data-msg-index="idx"
                        :class="{ 'is-user': msg.role === 'user', 'is-assistant': msg.role === 'assistant', 'is-error': !!msg.isError, 'is-pending-older': !!(msg.isPendingInProcess || msg.isPendingQueued || msg.isPendingOlder) }"
                    )
                        //- Assistant placeholder for the in-process request
                        .bq-bubble(v-if="msg.isPending && (msg.isPendingInProcess || msg.isPendingOlder)")
                            span Processing queued request...
                            span.bq-pending-note (In process)
                        //- Fallback "Thinking" bubble (shouldn't normally appear with the new
                        //- in-process / queued split, but kept for robustness)
                        .bq-bubble(v-else-if="msg.isPending")
                            span.bq-loader Thinking
                        .bq-bubble(v-else)
                            template(v-for="(part, pi) in parseMsgParts(msg.content)" :key="pi")
                                div.bq-md(
                                    v-if="part.type === 'html'"
                                    v-html="part.html"
                                    @click="onBubbleLinkClick"
                                )
                                a.bq-file-download(
                                    v-else-if="part.type === 'file'"
                                    :href="part.href"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                ) &#x2197; {{ part.filename }}
                                a.bq-link-button(
                                    v-else-if="part.type === 'link'"
                                    :href="part.href"
                                    :title="part.fullLabel || part.label"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :download="part.fullLabel || part.label"
                                    :class="{ 'is-expired': part.expired, 'is-refreshing': isLinkRefreshing(part) }"
                                    @click="onMessageLinkClick($event, part)"
                                ) &#x2197; {{ part.label }}{{ isLinkRefreshing(part) ? ' (fetching...)' : '' }}
                                span(v-else) {{ part.text }}
                            span.bq-pending-note(v-if="(msg.isPendingInProcess || msg.isPendingOlder) && !msg.isPending") (In process)
                            span.bq-pending-note(v-else-if="msg.isPendingQueued") (In queue)

                    .bq-message.is-assistant(v-if="sending && !typing")
                        .bq-bubble
                            span.bq-loader Thinking

            form.bq-input-row(@submit.prevent="sendMessage")
                .bq-attachment-warning(
                    v-if="attachments.length && attachmentsBudgetWarning"
                    role="alert"
                )
                    span.material-symbols-outlined.bq-attachment-warning-icon priority_high
                    span
                        | Attachments are ~{{ formatTokenCount(attachmentsTokenEstimate) }} tokens, which may exceed the ~{{ formatTokenCount(currentInputTokenBudget) }}-token per-request limit.
                        br
                        | Go to&nbsp;
                        router-link.bq-link(:to="`/my-projects/${currentService.id}/dbfile`") Files
                        |  page to upload more files.
                .bq-attachments(v-if="attachments.length" ref="attachmentsBox")
                    .bq-attachment(
                        v-for="a in visibleAttachments"
                        :key="a.id"
                        :title="attachmentTooltip(a)"
                        :class="{ 'is-uploading': a.status === 'uploading', 'is-done': a.status === 'done', 'is-error': a.status === 'error', 'is-clickable': a.status === 'done' && a.kind === 'file' && !!a.uploadedUrl }"
                        :style="a.status === 'uploading' && a.progress != null ? { '--att-progress': a.progress + '%' } : {}"
                        @click="onAttachmentClick(a)"
                    )
                        span.bq-attachment-arrow(v-if="a.status === 'done' && a.kind === 'file'" aria-hidden="true") &#8599;
                        svg.bq-attachment-icon(v-else width="16" height="16")
                            use(:xlink:href="a.kind === 'folder' ? '/material-icon.svg#icon-folder-fill' : '/material-icon.svg#icon-file-text'")
                        span.bq-attachment-name {{ a.name }}
                        span.bq-attachment-meta(v-if="a.kind === 'folder'") &nbsp;({{ a.files.length }})
                        span.bq-attachment-meta(v-else-if="a.status === 'uploading' && a.progress != null") &nbsp;{{ a.progress }}%
                        span.bq-attachment-meta(v-else-if="a.status === 'error'") &nbsp;failed
                        button.bq-attachment-remove(
                            v-if="a.status !== 'done'"
                            type="button"
                            :aria-label="a.status === 'uploading' ? 'Cancel upload' : 'Remove attachment'"
                            :title="a.status === 'uploading' ? 'Cancel upload' : 'Remove'"
                            @click.stop="removeAttachment(a.id)"
                            data-track="agent_attachment_remove"
                        ) &times;
                    .bq-attachment.bq-attachment-more(v-if="hiddenAttachmentsCount > 0" :title="hiddenAttachmentsTooltip")
                        span.bq-attachment-name ...({{ hiddenAttachmentsCount }}) more
                .bq-input-wrap
                    button.bq-attach-btn(
                        type="button"
                        aria-label="Attach files"
                        title="Attach files"
                        :disabled="!chatEnabled || sending || typing || initialGateLoading || uploadingAttachments || hasPendingMessage"
                        @click="openAttachPicker"
                        data-track="agent_attach"
                    )
                        svg(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                            path(d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48")
                    input.bq-attach-input(
                        ref="attachInput"
                        type="file"
                        multiple
                        @change="onAttachFromInput"
                    )
                    textarea.bq-input(
                        ref="inputBox"
                        v-model="prompt"
                        rows="1"
                        :placeholder="sending || typing || uploadingAttachments || hasPendingMessage ? 'Request in process. Please wait...' : 'Ask anything about the project...'"
                        :disabled="!chatEnabled || sending || typing || initialGateLoading || uploadingAttachments || hasPendingMessage"
                        @keydown.enter.exact="handlePromptEnter"
                        @compositionstart="isPromptComposing = true"
                        @compositionend="handlePromptCompositionEnd"
                        @input="autoGrowInput"
                    )
                button.btn(type="submit" :disabled="!canSend" data-track="agent_send")
                    span.bq-loader(v-if="sending || typing || uploadingAttachments || hasPendingMessage")
                    template(v-else) Send

    //- modal :: clear chat history
    Modal(:open="showClearHistoryModal", @close="() => { if (!clearingHistory) showClearHistoryModal = false }")
        .bq-modal-delete
            .bq-modal-delete-header
                span.material-symbols-outlined.bq-modal-delete-icon delete
                span Clear chat history
            .bq-modal-delete-body
                p Hide all previous messages from this {{ platformLabel }} chat?
                p.bq-modal-delete-warn New messages will appear normally. Existing history will be hidden from view and from the AI's context, but the records remain on the server.
            .bq-modal-delete-footer
                template(v-if="clearingHistory")
                    span.bq-loader Clearing
                template(v-else)
                    button.bq-modal-delete-btn.bq-modal-delete-btn--cancel(
                        @click="showClearHistoryModal = false"
                    ) Cancel
                    button.bq-modal-delete-btn.bq-modal-delete-btn--delete(
                        @click="confirmClearHistory"
                        data-track="agent_clearHistoryModal_clear"
                    ) Clear

    //- modal :: enter AI api key
    Modal(:open="apiKeyModalOpen" @close="() => { if (!savingApiKey) apiKeyModalOpen = false }")
        .bq-modal-wrap
            .bq-modal-close(@click="apiKeyModalOpen = false" :class="{ disabled: savingApiKey }") &times;
            form.bq-modal-step(@submit.prevent="saveApiKey")
                h2 API Key
                p.bq-modal-desc Enter your {{ platformLabel }} API key.
                textarea.bq-modal-textarea(
                    ref="apiKeyInputEl"
                    :disabled="savingApiKey || null"
                    placeholder="sk-..."
                    :value="apiKeyInput"
                    rows="3"
                    @input="e => apiKeyInput = e.target.value"
                )
                button.btn(
                    type="submit"
                    :class="{ 'bq-btn--loading': savingApiKey }"
                    :disabled="savingApiKey || !apiKeyInput.trim()"
                    data-track="agent_apiKeyModal_save"
                )
                    span.bq-loader(v-if="savingApiKey")
                    template(v-else) Save
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { SkapiError } from "skapi-js";
import { skapi } from "@/main";
import mime from "mime";
import { marked } from "marked";
import { aiApiKeyCache, aiChatHistoryCache, aiModelCache, agentHistoryRefreshTrigger, agentHistoryRefreshLastSeen, currentService, pendingAgentRequests, pullServices, serviceClientSecrets, saveAiApiKeyPrivately, loadAiApiKeyPrivately } from "@/views/project-list";
import { hostDomain } from "@/code/service";
import { removeFullScreenLoader } from "@/components/jsComponents";
import BunnyLoader from "@/components/bunnyLoader.vue";
import Modal from "@/components/modal.vue";
import chatgptLogo from "@/assets/img/logo/ChatGPT-Logo.png";
import claudeLogo from "@/assets/img/logo/claude-ai-symbol.svg";
import {
    callClaudeWithPublicMcp,
    callOpenAIWithPublicMcp,
    DEFAULT_CLAUDE_MODEL,
    DEFAULT_OPENAI_MODEL,
    extractClaudeText,
    extractOpenAIText,
    getChatHistory,
    listClaudeModels,
    listOpenAIModels,
    notifyAgentSaveAttachment,
    BG_INDEXING_QUEUE_SUFFIX,
    type ClaudeMessage,
} from "@/code/ai_agent";

type ChatMessage = ClaudeMessage & {
    isError?: boolean;
    isPending?: boolean;
    // The single oldest pending request — the one currently being processed
    // by the upstream provider. Renders yellow with "Processing previous
    // request..." on the assistant placeholder and "(In process)" labels.
    isPendingInProcess?: boolean;
    // Pendings that are queued behind the in-process one. Renders yellow with
    // a "(In queue)" label below the user message bubble. No assistant
    // placeholder is emitted for queued items to keep the chat compact.
    isPendingQueued?: boolean;
    // Stable server-side identifier (PollingResult.id) for messages that
    // originated from getChatHistory. The id stays the same across the
    // pending→resolved lifecycle, so we can detect when a previously-pending
    // entry has just resolved and scroll its bubble into view (instead of
    // resetting the user's scroll position to the bottom on every poll).
    _serverItemId?: string;
    // Back-compat: kept so cached histories from older builds don't break
    // when re-hydrated. Treated as `isPendingInProcess` for rendering.
    isPendingOlder?: boolean;
    // True for messages that originated from the background-indexing queue
    // (notifyAgentSaveAttachment). These are shown in the chat for visibility
    // but must not block the user from sending new chat messages.
    isBackgroundTask?: boolean;
};
type AiPlatform = "claude" | "openai" | "none";

const parseAiAgentValue = (value: string | null | undefined) => {
    const raw = (value || "").trim();
    const [platform, model] = raw.includes("#") ? raw.split("#") : [raw, ""];
    const normalizedPlatform: AiPlatform =
        platform === "claude" || platform === "openai" ? platform : "none";

    return {
        raw,
        platform: normalizedPlatform,
        model: model || "",
        hasPlatform: normalizedPlatform !== "none",
    };
};

const initialAiAgentState = parseAiAgentValue(currentService.value?.service?.ai_agent);

const prompt = ref("");
const isPromptComposing = ref(false);
const sending = ref(false);
const typing = ref(false);

type AttachmentUploadStatus = "pending" | "uploading" | "done" | "error";

type AgentAttachment =
    | {
          id: string;
          kind: "file";
          name: string;
          file: File;
          status?: AttachmentUploadStatus;
          progress?: number; // 0..100
          errorMessage?: string;
          uploadedUrl?: string;
      }
    | {
          id: string;
          kind: "folder";
          name: string;
          files: { file: File; path: string }[];
          status?: AttachmentUploadStatus;
          progress?: number; // 0..100 aggregate
          errorMessage?: string;
          uploadedUrls?: { path: string; url: string }[];
      };

const attachments = ref<AgentAttachment[]>([]);
const dragHere = ref(false);
let dragDepth = 0;
const uploadingAttachments = ref(false);

// True when a user-initiated chat request is still pending (not yet resolved).
// Background-indexing tasks (isBackgroundTask) are excluded so they don't
// block the chat input — they run in a separate parallel queue.
const hasPendingMessage = computed(() =>
    messages.value.some((m) => m.isPending && !m.isBackgroundTask)
);

const canSend = computed(() => {
    if (!chatEnabled.value || sending.value || typing.value || initialGateLoading.value) {
        return false;
    }
    if (hasPendingMessage.value) return false;
    if (uploadingAttachments.value) return false;
    if (attachmentsExceedBudget.value) return false;
    if (prompt.value.trim()) return true;
    if (attachments.value.length === 0) return false;
    // Allow send on attachments-only as long as at least one is not in a
    // permanent error state.
    return attachments.value.some((a) => a.status !== "error");
});

// Chip overflow handling: when the attachments wrap row would exceed
// 30% of the chat container's height, hide the overflowing chips and
// surface a "...(n) more" pseudo-chip in their place.
const ATTACHMENTS_MAX_HEIGHT_RATIO = 0.3;
const chatBox = ref<HTMLElement | null>(null);
const attachmentsBox = ref<HTMLElement | null>(null);
// Number.MAX_SAFE_INTEGER means "no truncation" (show all).
const visibleAttachmentCount = ref(Number.MAX_SAFE_INTEGER);
let attachmentsResizeObserver: ResizeObserver | null = null;
let attachmentsRecomputeFrame = 0;

const visibleAttachments = computed(() =>
    sortedAttachments.value.slice(0, visibleAttachmentCount.value)
);

const hiddenAttachmentsCount = computed(() =>
    Math.max(0, sortedAttachments.value.length - visibleAttachments.value.length)
);

const hiddenAttachmentsTooltip = computed(() => {
    const hidden = sortedAttachments.value.slice(visibleAttachments.value.length);
    return hidden.map((a) => (a.kind === "folder" ? `${a.name}/` : a.name)).join("\n");
});

// Display order: in-flight uploads first (so newly added files always appear
// at the head of the list), then everything else. This keeps the truncation
// "...(n) more" tail on already-finished items rather than hiding the file
// the user just attached. Within each group we preserve insertion order so
// the most recent upload sits at index 0 only if it actually starts last.
const ATTACHMENT_STATUS_PRIORITY: Record<string, number> = {
    uploading: 0,
    pending: 1,
    error: 2,
    done: 3,
};
const sortedAttachments = computed(() => {
    const list = attachments.value.map((a, i) => ({ a, i }));
    list.sort((x, y) => {
        const px = ATTACHMENT_STATUS_PRIORITY[x.a.status] ?? 99;
        const py = ATTACHMENT_STATUS_PRIORITY[y.a.status] ?? 99;
        if (px !== py) return px - py;
        // Newer uploads bubble to the front within the uploading group; for
        // the rest preserve original insertion order.
        if (px === 0) return y.i - x.i;
        return x.i - y.i;
    });
    return list.map((e) => e.a);
});

const newAttachmentId = () => `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Stable identity for an attachment so repeat drops of the same file/folder
// do not stack duplicate chips. Files use name+size+lastModified (a stronger
// signal than name alone, which is the same heuristic browsers expose for
// duplicate detection on `<input type="file">`). Folders use name + file
// count + total size of their contents.
const attachmentKey = (a: AgentAttachment): string => {
    if (a.kind === "file") {
        return `f|${a.name}|${a.file.size}|${a.file.lastModified}`;
    }
    let total = 0;
    for (const entry of a.files) total += entry.file.size;
    return `d|${a.name}|${a.files.length}|${total}`;
};

const attachmentTooltip = (a: AgentAttachment) => {
    if (a.kind === "folder") {
        return `${a.name}/ — ${a.files.length} file${a.files.length === 1 ? "" : "s"}`;
    }
    return `${a.name} (${a.file.size} bytes)`;
};

// Per-attachment abort handles for in-flight uploads. Populated by
// uploadSingleAttachment via the progress callback's `abort` function.
const attachmentAborters = new Map<string, () => void>();
// Track ids the user explicitly cancelled so the upload loop can swallow the
// resulting abort error and continue with the next attachment instead of
// failing the whole batch.
const cancelledAttachmentIds = new Set<string>();

const removeAttachment = (id: string) => {
    const target = attachments.value.find((a) => a.id === id);
    if (target && target.status === "uploading") {
        cancelledAttachmentIds.add(id);
        const ab = attachmentAborters.get(id);
        if (ab) {
            try { ab(); } catch { /* ignore */ }
        }
    }
    attachmentAborters.delete(id);
    attachments.value = attachments.value.filter((a) => a.id !== id);
};

const clearAttachments = () => {
    attachments.value = [];
};

// Open a finished file attachment in a new tab when the chip itself is
// clicked. Folders and in-flight/errored attachments are not clickable.
const onAttachmentClick = (a: AgentAttachment) => {
    if (a.status !== "done" || a.kind !== "file" || !a.uploadedUrl) return;
    window.open(a.uploadedUrl, "_blank", "noopener,noreferrer");
};

// Upload an attachment (single file or folder) to the service's db storage
// at the bucket root and return the resulting list of {filename, url}.
// Mutates `att` to track progress/status so the chip can render the bar.
const ATTACHMENT_URL_EXPIRES_SECONDS = 60 * 10; // 10m

const getAttachmentTemporaryUrlOptions = (_platform: AiPlatform) => {
    // Both Claude and OpenAI fetch the file from the URL we hand them, so a
    // temporary CDN URL works for both. generate_redirect_url is intentionally
    // left false because the redirect endpoint requires the caller's session.
    return {
        generate_temporary_cdn_url: true,
    };
};

const resolveAttachmentMemberUrl = async (
    storagePath: string,
    displayPath: string,
    platform: AiPlatform = selectedAiPlatform.value
) => {
    // storagePath is the exact db key the file was uploaded under (filename or
    // folder/filename). Pass it through verbatim. Do NOT slice/trim - earlier
    // versions did `slice(-2)` which corrupted deeper folder paths and, when
    // re-resolving a link extracted from a CDN URL, accidentally used the
    // service's content hash as if it were a folder.
    const plainPath = normalizeAttachmentPathCandidate(storagePath) || storagePath;
    const tu = await currentService.value.getTemporaryUrl({
        request: "get-db",
        path: plainPath,
        expires: ATTACHMENT_URL_EXPIRES_SECONDS,
        ...getAttachmentTemporaryUrlOptions(platform),
    });

    return {
        name: displayPath,
        url: typeof tu === "string" ? tu : tu.url,
    };
};

const uploadSingleAttachment = async (
    att: AgentAttachment
): Promise<{ name: string; url: string }[]> => {
    // Collect the attachment members first. Each member is then uploaded
    // individually so `EXISTS` can fall back to a temporary URL instead of
    // aborting the whole attachment upload.
    const members: { file: File; relPath: string; storagePath: string }[] = [];

    if (att.kind === "file") {
        members.push({
            file: att.file,
            relPath: att.name,
            storagePath: att.name,
        });
    } else {
        for (const entry of att.files) {
            const relPath = entry.path; // already includes folder prefix
            members.push({
                file: entry.file,
                relPath,
                storagePath: relPath,
            });
        }
    }

    const totalCount = members.length;
    if (totalCount === 0) return [];

    att.status = "uploading";
    att.progress = 0;
    delete att.errorMessage;

    const urls: { name: string; url: string }[] = [];
    for (let index = 0; index < members.length; index++) {
        const member = members[index];
        const form = new FormData();
        const baseName = member.storagePath.split("/").pop() || member.file.name;
        const contentTypeMapping: Record<string, string> = {};

        form.append("files[]", member.file, member.storagePath);

        if (!member.file.type) {
            const ct = mime.getType(baseName);
            if (ct) {
                contentTypeMapping[baseName] = ct;
            }
        }

        try {
            const result = (await currentService.value.uploadHostFiles(form, {
                contentTypeMapping,
                target: "db",
                check_existence: true,
                progress: (p: any) => {
                    // Capture the abort handle on every callback so cancel
                    // works at any point during the upload (loadstart,
                    // progress, etc.). Cleared in finally below.
                    if (typeof p.abort === "function") {
                        attachmentAborters.set(att.id, p.abort);
                    }
                    if (p.status !== "progress") return;

                    const currentPct = Math.max(
                        0,
                        Math.min(100, Math.floor(p.progress || 0))
                    );
                    const overall = Math.floor(
                        ((index + currentPct / 100) / totalCount) * 100
                    );
                    att.progress = Math.max(0, Math.min(100, overall));
                },
            })) as { completed: File[]; failed: File[] };

            if (result.failed?.length) {
                throw new Error(`Upload failed for ${member.relPath}`);
            }
        } catch (err: any) {
            // If the user cancelled this attachment, surface that immediately
            // so the outer loop can skip to the next one.
            if (cancelledAttachmentIds.has(att.id)) {
                throw err;
            }
            const code = err instanceof SkapiError ? err.code : err?.code;
            if (code !== "EXISTS") {
                throw err;
            }
        } finally {
            attachmentAborters.delete(att.id);
        }

        if (cancelledAttachmentIds.has(att.id)) {
            // Cancelled mid-batch (folder member). Stop processing more
            // members and let the outer loop handle the cancel.
            throw new Error("Upload cancelled");
        }

        let resolvedUrl: string | undefined;
        try {
            const resolved = await resolveAttachmentMemberUrl(
                member.storagePath,
                member.relPath
            );
            urls.push(resolved);
            resolvedUrl = resolved.url;
        } catch (err) {
            console.error("getTemporaryUrl failed for", member.storagePath, err);
        }

        // Notify the AI agent to index the uploaded file into the record
        // database, exactly like dbfile.vue's onUploadedNotifyAgent does.
        if (resolvedUrl) {
            try {
                const raw = (currentService.value?.service?.ai_agent || "").trim();
                if (raw) {
                    const [platformRaw, modelRaw] = raw.includes("#") ? raw.split("#") : [raw, ""];
                    const platform = (platformRaw === "claude" || platformRaw === "openai")
                        ? (platformRaw as "claude" | "openai")
                        : null;
                    if (platform) {
                        const serviceId = currentService.value.id;
                        const indexingPromise = notifyAgentSaveAttachment({
                            platform,
                            model: modelRaw || undefined,
                            service: serviceId,
                            owner: currentService.value.owner,
                            serviceName: currentService.value?.service?.name,
                            serviceDescription: currentService.value?.service?.description,
                            attachment: {
                                name: member.file.name,
                                storagePath: member.storagePath,
                                mime: member.file.type || mime.getType(member.file.name) || undefined,
                                size: member.file.size,
                                url: resolvedUrl,
                            },
                        });
                        if (indexingPromise && typeof (indexingPromise as Promise<unknown>).catch === "function") {
                            (indexingPromise as Promise<unknown>).catch((err) => {
                                console.error("notifyAgentSaveAttachment failed", err);
                            });
                        }
                        agentHistoryRefreshTrigger[serviceId] = (agentHistoryRefreshTrigger[serviceId] || 0) + 1;
                    }
                }
            } catch (err) {
                console.error("notifyAgentSaveAttachment failed", err);
            }
        }

        att.progress = Math.max(
            att.progress || 0,
            Math.floor(((index + 1) / totalCount) * 100)
        );
    }

    if (!urls.length) {
        throw new Error(`Upload failed for ${att.name}`);
    }

    att.progress = 100;
    att.status = "done";
    if (att.kind === "file") {
        att.uploadedUrl = urls[0]?.url;
    } else {
        att.uploadedUrls = urls.map((u) => ({ path: u.name, url: u.url }));
    }
    return urls;
};

const uploadPendingAttachments = async (): Promise<{ name: string; url: string }[]> => {
    const all = attachments.value.filter((a) => a.status !== "done");
    if (all.length === 0) {
        // already-uploaded attachments contribute their cached URLs
        const cached: { name: string; url: string }[] = [];
        for (const a of attachments.value) {
            if (a.kind === "file" && a.uploadedUrl) {
                cached.push({ name: a.name, url: a.uploadedUrl });
            } else if (a.kind === "folder" && a.uploadedUrls) {
                cached.push(
                    ...a.uploadedUrls.map((u) => ({ name: u.path, url: u.url }))
                );
            }
        }
        return cached;
    }

    uploadingAttachments.value = true;
    const collected: { name: string; url: string }[] = [];

    try {
        // Snapshot so removals (e.g. user cancelling the in-flight upload)
        // don't disrupt iteration of the live reactive array.
        const queue = [...attachments.value];
        for (const att of queue) {
            // The attachment may have been removed (cancelled) before we
            // reached it.
            if (!attachments.value.some((a) => a.id === att.id)) continue;
            if (att.status === "done") {
                if (att.kind === "file" && att.uploadedUrl) {
                    collected.push({ name: att.name, url: att.uploadedUrl });
                } else if (att.kind === "folder" && att.uploadedUrls) {
                    collected.push(
                        ...att.uploadedUrls.map((u) => ({ name: u.path, url: u.url }))
                    );
                }
                continue;
            }
            try {
                const urls = await uploadSingleAttachment(att);
                collected.push(...urls);
            } catch (err: any) {
                if (cancelledAttachmentIds.has(att.id)) {
                    // User cancelled this one; skip to the next attachment.
                    cancelledAttachmentIds.delete(att.id);
                    attachmentAborters.delete(att.id);
                    continue;
                }
                att.status = "error";
                att.errorMessage =
                    err && err.message ? String(err.message) : "Upload failed";
                throw err;
            }
        }
    } finally {
        uploadingAttachments.value = false;
    }

    return collected;
};

// Iteratively reduce the number of visible chips until the wrap container's
// scrollHeight no longer exceeds 30% of the chat container's height. The
// "...(n) more" chip renders as soon as we hide one, so each tick stabilizes
// to the largest count that fits *with* the overflow indicator present.
const recomputeAttachmentOverflow = async () => {
    const total = attachments.value.length;
    if (!total) {
        visibleAttachmentCount.value = Number.MAX_SAFE_INTEGER;
        return;
    }

    visibleAttachmentCount.value = total;
    await nextTick();

    const chat = chatBox.value;
    const box = attachmentsBox.value;
    if (!chat || !box) return;

    const maxHeight = chat.clientHeight * ATTACHMENTS_MAX_HEIGHT_RATIO;
    if (maxHeight <= 0) return;

    let count = total;
    while (count > 0 && box.scrollHeight > maxHeight) {
        count--;
        visibleAttachmentCount.value = count;
        await nextTick();
    }
};

const scheduleAttachmentRecompute = () => {
    cancelAnimationFrame(attachmentsRecomputeFrame);
    attachmentsRecomputeFrame = requestAnimationFrame(() => {
        void recomputeAttachmentOverflow();
    });
};

watch(
    () => attachments.value.length,
    () => {
        scheduleAttachmentRecompute();
    }
);

// Recursively reads a FileSystemEntry (file or directory) into a flat list of
// files with their relative paths. Paths are prefixed with the provided prefix
// (used for nested directory traversal).
const readEntry = (entry: any, prefix = ""): Promise<{ file: File; path: string }[]> => {
    return new Promise((resolve) => {
        if (!entry) {
            resolve([]);
            return;
        }

        if (entry.isFile) {
            entry.file(
                (file: File) => {
                    resolve([{ file, path: prefix + file.name }]);
                },
                () => resolve([])
            );
            return;
        }

        if (entry.isDirectory) {
            const reader = entry.createReader();
            const all: { file: File; path: string }[] = [];
            const readBatch = () => {
                reader.readEntries(async (entries: any[]) => {
                    if (!entries.length) {
                        resolve(all);
                        return;
                    }
                    const groups = await Promise.all(
                        entries.map((e) => readEntry(e, prefix + entry.name + "/"))
                    );
                    groups.forEach((g) => all.push(...g));
                    // readEntries only returns a chunk; keep reading until empty.
                    readBatch();
                }, () => resolve(all));
            };
            readBatch();
            return;
        }

        resolve([]);
    });
};

const onChatDragEnter = (e: DragEvent) => {
    if (!chatEnabled.value) return;
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
    dragDepth++;
    dragHere.value = true;
};

const onChatDragOver = (e: DragEvent) => {
    if (!chatEnabled.value) return;
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
    e.dataTransfer.dropEffect = "copy";
    dragHere.value = true;
};

const onChatDragLeave = (_e: DragEvent) => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) {
        dragHere.value = false;
    }
};

const onChatDrop = async (e: DragEvent) => {
    dragDepth = 0;
    dragHere.value = false;
    if (!chatEnabled.value) return;
    if (!e.dataTransfer) return;

    const items = e.dataTransfer.items;
    const added: AgentAttachment[] = [];

    if (items && items.length) {
        const entries: any[] = [];
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.kind !== "file") continue;
            const entry = (it as any).webkitGetAsEntry ? (it as any).webkitGetAsEntry() : null;
            entries.push(entry || it.getAsFile());
        }

        for (const entry of entries) {
            if (!entry) continue;
            if (entry instanceof File) {
                added.push({
                    id: newAttachmentId(),
                    kind: "file",
                    name: entry.name,
                    file: entry,
                });
                continue;
            }

            if (entry.isFile) {
                const files = await readEntry(entry);
                if (files[0]) {
                    added.push({
                        id: newAttachmentId(),
                        kind: "file",
                        name: files[0].file.name,
                        file: files[0].file,
                    });
                }
            } else if (entry.isDirectory) {
                const files = await readEntry(entry);
                added.push({
                    id: newAttachmentId(),
                    kind: "folder",
                    name: entry.name,
                    files,
                });
            }
        }
    } else if (e.dataTransfer.files && e.dataTransfer.files.length) {
        for (const f of Array.from(e.dataTransfer.files)) {
            added.push({ id: newAttachmentId(), kind: "file", name: f.name, file: f });
        }
    }

    if (added.length) {
        appendAttachments(added);
    }
};

const attachInput = ref<HTMLInputElement | null>(null);

const appendAttachments = (added: AgentAttachment[]) => {
    if (!added.length) return;
    const existingKeys = new Set(attachments.value.map(attachmentKey));
    const seenInBatch = new Set<string>();
    const deduped = added.filter((a) => {
        const k = attachmentKey(a);
        if (existingKeys.has(k) || seenInBatch.has(k)) return false;
        seenInBatch.add(k);
        return true;
    });
    if (deduped.length) {
        attachments.value = attachments.value.concat(deduped);
    }
};

const openAttachPicker = () => {
    if (!chatEnabled.value || sending.value || typing.value || uploadingAttachments.value) {
        return;
    }
    attachInput.value?.click();
};

const onAttachFromInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length) {
        const added: AgentAttachment[] = files.map((f) => ({
            id: newAttachmentId(),
            kind: "file",
            name: f.name,
            file: f,
        }));
        appendAttachments(added);
    }
    // reset so selecting the same file again still fires `change`
    input.value = "";
};
// Drives the animated dots in [generating file.ext...] placeholders.
// Ticks from 0..2 while any typewriter is active.
const dotTick = ref(0);
let dotTickTimer: ReturnType<typeof setInterval> | null = null;
const historyLoadDotTick = ref(0);
let historyLoadDotTickTimer: ReturnType<typeof setInterval> | null = null;
let typingAbort = false;
const initialGateLoading = ref(initialAiAgentState.hasPlatform);
const selectedAiPlatform = ref<AiPlatform>(initialAiAgentState.platform);
const selectedAiModel = ref(initialAiAgentState.model);
const hasAiPlatform = ref(initialAiAgentState.hasPlatform);
const hasAiApiKey = ref(false);
const messages = ref<ChatMessage[]>([]);
const agentRoot = ref<HTMLElement | null>(null);
const messagesBox = ref<HTMLElement | null>(null);
const inputBox = ref<HTMLTextAreaElement | null>(null);
const loadingHistory = ref(false);
const loadingOlderHistory = ref(false);
const historyEndOfList = ref(false);
const historyStartKeyHistory = ref<string[]>([]);
let gateRefreshToken = 0;
let historyRequestToken = 0;
let viewportHeightFrame = 0;
let pendingPollTimer: ReturnType<typeof setTimeout> | null = null;
const PENDING_POLL_INTERVAL_MS = 1000;
let agentUnmounted = false;

const chatEnabled = computed(() => {
    return hasAiPlatform.value && hasAiApiKey.value;
});

const showInitialLoading = computed(() => {
    return initialGateLoading.value && hasAiPlatform.value;
});

const showHistoryLoadingIndicator = computed(() => {
    return loadingOlderHistory.value && !initialGateLoading.value && messages.value.length > 0;
});

const historyLoadingLabel = computed(() => {
    return `Fetching history${".".repeat((historyLoadDotTick.value % 3) + 1)}`;
});

const platformLabel = computed(() => {
    if (selectedAiPlatform.value === "claude") {
        return "Claude";
    }

    if (selectedAiPlatform.value === "openai") {
        return "ChatGPT";
    }

    return "AI";
});

const platformLogo = computed(() => {
    if (selectedAiPlatform.value === "claude") return claudeLogo;
    if (selectedAiPlatform.value === "openai") return chatgptLogo;
    return "";
});

const agentTitle = computed(() => {
    if (!hasAiPlatform.value) return "No AI platform";
    return `${platformLabel.value} Agent`;
});

const projectName = computed(() => {
    return currentService.value?.service?.name || "";
});

const selectedAiModelDisplay = computed(() => {
    if (selectedAiModel.value) {
        return selectedAiModel.value;
    }

    if (selectedAiPlatform.value === "claude") {
        return DEFAULT_CLAUDE_MODEL;
    }

    if (selectedAiPlatform.value === "openai") {
        return DEFAULT_OPENAI_MODEL;
    }

    return "";
});

// Available models for the currently-selected platform. Sourced from
// `aiModelCache` (shared with project settings) so we don't refetch.
type AiModel = { id: string; display_name?: string };
const availableModels = ref<AiModel[]>([]);
const fetchingModels = ref(false);
const savingModel = ref(false);
const modelMenuOpen = ref(false);
const isMobile = ref(false);
// Set to true when fetching the model list fails despite an API key being
// configured — the only realistic cause is an invalid/revoked key. Cleared on
// platform change and on successful save/refetch.
const apiKeyInvalid = ref(false);
let modelFetchToken = 0;

const syncMobile = () => {
    isMobile.value = window.matchMedia("(max-width: 600px)").matches;
    if (isMobile.value) modelMenuOpen.value = false;
};

const toggleModelMenu = () => {
    if (isMobile.value) return;
    modelMenuOpen.value = !modelMenuOpen.value;
};

const closeModelMenu = () => {
    modelMenuOpen.value = false;
};

// Resolve the model id we should treat as currently selected: the saved one,
// or the platform default if the saved one is empty/missing from the list.
const effectiveModel = computed(() => {
    const list = availableModels.value;
    if (!list.length) return selectedAiModel.value || "";

    const saved = selectedAiModel.value;
    if (saved && list.some((m) => m.id === saved)) return saved;

    const platformDefault =
        selectedAiPlatform.value === "claude"
            ? DEFAULT_CLAUDE_MODEL
            : selectedAiPlatform.value === "openai"
                ? DEFAULT_OPENAI_MODEL
                : "";

    if (platformDefault && list.some((m) => m.id === platformDefault)) {
        return platformDefault;
    }

    return list[0]?.id || "";
});

const effectiveModelLabel = computed(() => {
    const id = effectiveModel.value;
    if (!id) return "Select model";
    const match = availableModels.value.find((m) => m.id === id);
    return match?.display_name || match?.id || id;
});

const loadAvailableModels = async () => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") {
        availableModels.value = [];
        return;
    }

    const token = ++modelFetchToken;
    const cached = aiModelCache[platform];
    if (cached && cached.length) {
        availableModels.value = cached;
        return;
    }

    fetchingModels.value = true;
    try {
        const serviceId = currentService.value?.id;
        const owner = currentService.value?.owner;
        if (!serviceId || !owner) return;

        const response = platform === "claude"
            ? await listClaudeModels(serviceId, owner)
            : await listOpenAIModels(serviceId, owner);

        if (token !== modelFetchToken || selectedAiPlatform.value !== platform) return;

        const models: AiModel[] = response?.data || [];
        // Treat a missing/empty model list while an API key is configured as
        // an invalid-key signal. Some skapi clientSecretRequest paths surface
        // upstream auth failures by resolving with no `data` instead of
        // throwing, so the catch block alone isn't sufficient.
        if (!models.length && hasAiApiKey.value) {
            availableModels.value = [];
            apiKeyInvalid.value = true;
            return;
        }
        aiModelCache[platform] = models;
        availableModels.value = models;
        apiKeyInvalid.value = false;
    } catch {
        if (token === modelFetchToken) {
            availableModels.value = [];
            // Only treat this as an invalid-key signal when a key is actually
            // configured for the current platform. Otherwise the missing-key
            // overlay already covers the empty-models case.
            if (hasAiApiKey.value) apiKeyInvalid.value = true;
        }
    } finally {
        if (token === modelFetchToken) fetchingModels.value = false;
    }
};

watch(
    () => [chatEnabled.value, selectedAiPlatform.value],
    () => {
        modelMenuOpen.value = false;
        if (chatEnabled.value) void loadAvailableModels();
    },
    { immediate: true }
);

const onModelSelect = async (modelId: string) => {
    modelMenuOpen.value = false;
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return;
    if (!modelId || modelId === selectedAiModel.value) return;

    const previous = selectedAiModel.value;
    selectedAiModel.value = modelId;
    savingModel.value = true;
    try {
        await currentService.value.setServiceOption({
            ai_agent: `${platform}#${modelId}`,
        });
    } catch (err) {
        // revert on failure
        selectedAiModel.value = previous;
        console.error("Failed to update AI model", err);
    } finally {
        savingModel.value = false;
    }
};

const onModelSelectFromNative = (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    if (target) void onModelSelect(target.value);
};

// AI platform selector. Mirrors the model selector pattern: writes the new
// platform to the service config (clearing the model so the platform's
// default is used until the user picks one), then relies on the existing
// `currentService.service.ai_agent` watcher to call refreshGate(), which
// re-pulls the API key state and reloads the model list for the new platform.
type PlatformOption = { id: "claude" | "openai"; label: string; logo: string };
const platformOptions: PlatformOption[] = [
    { id: "claude", label: "Claude", logo: claudeLogo },
    { id: "openai", label: "ChatGPT", logo: chatgptLogo },
];
const platformMenuOpen = ref(false);
const savingPlatform = ref(false);

const togglePlatformMenu = () => {
    if (isMobile.value) return;
    platformMenuOpen.value = !platformMenuOpen.value;
    if (platformMenuOpen.value) modelMenuOpen.value = false;
};

const closePlatformMenu = () => {
    platformMenuOpen.value = false;
};

const onPlatformSelect = async (platform: "claude" | "openai") => {
    platformMenuOpen.value = false;
    if (!platform) return;
    if (platform === selectedAiPlatform.value) return;

    const previousPlatform = selectedAiPlatform.value;
    const previousModel = selectedAiModel.value;
    selectedAiPlatform.value = platform;
    selectedAiModel.value = "";
    apiKeyInvalid.value = false;
    savingPlatform.value = true;
    // Show the hopping-rabbit chatbox overlay (same one used during the
    // initial gate load) while the platform swap + gate refresh runs.
    initialGateLoading.value = true;
    try {
        await currentService.value.setServiceOption({
            ai_agent: platform,
        });
        
        savingPlatform.value = false;

        // Force the gate to re-resolve immediately so the overlay stays up
        // until API key + model list are ready for the new platform.
        await refreshGate();
    } catch (err) {
        // revert on failure
        selectedAiPlatform.value = previousPlatform;
        selectedAiModel.value = previousModel;
        initialGateLoading.value = false;
        console.error("Failed to update AI platform", err);
    } finally {
        savingPlatform.value = false;
    }
};

const onPlatformSelectFromNative = (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    const value = target?.value;
    if (value === "claude" || value === "openai") void onPlatformSelect(value);
};

// API key entry overlay + modal. Mirrors service.vue's saveAiApiKey flow:
// registerClientSecret → setServiceOption(ai_agent: <platform>) → refresh
// the local API-key cache and re-run refreshGate so the chat unlocks.
const apiKeyModalOpen = ref(false);
const apiKeyInput = ref("");
const apiKeyInputEl = ref<HTMLTextAreaElement | null>(null);
const savingApiKey = ref(false);
const loadingApiKey = ref(false);
const apiKeyLoadingDots = ref(".");
let apiKeyLoadingDotsTimer: ReturnType<typeof setInterval> | null = null;
const startApiKeyDots = () => {
    if (apiKeyLoadingDotsTimer) return;
    let n = 0;
    apiKeyLoadingDots.value = ".";
    apiKeyLoadingDotsTimer = setInterval(() => {
        n = (n + 1) % 3;
        apiKeyLoadingDots.value = ".".repeat(n + 1);
    }, 350);
};
const stopApiKeyDots = () => {
    if (apiKeyLoadingDotsTimer) {
        clearInterval(apiKeyLoadingDotsTimer);
        apiKeyLoadingDotsTimer = null;
    }
};

const apiKeyDocsUrl = computed(() => {
    if (selectedAiPlatform.value === "claude") return "https://platform.claude.com";
    if (selectedAiPlatform.value === "openai") return "https://platform.openai.com/";
    return "";
});

const openApiKeyModal = async () => {
    if (!hasAiPlatform.value || savingApiKey.value || loadingApiKey.value) return;
    const platform = selectedAiPlatform.value;
    const serviceId = currentService.value?.id;
    const cachedKey =
        (platform === "claude" || platform === "openai") && serviceId
            ? aiApiKeyCache[serviceId]?.[platform] ||
              serviceClientSecrets[serviceId]?.list?.[platform]?.client_secret ||
              ""
            : "";
    let prefill = cachedKey || "";
    // If we don't have a current key for this service+platform, await the
    // privately-stored last key before opening the dialog so the input is
    // already populated when the user sees it.
    if (!prefill && (platform === "claude" || platform === "openai")) {
        loadingApiKey.value = true;
        startApiKeyDots();
        try {
            prefill = (await loadAiApiKeyPrivately(platform)) || "";
        } finally {
            loadingApiKey.value = false;
            stopApiKeyDots();
        }
    }
    apiKeyInput.value = prefill;
    apiKeyModalOpen.value = true;
    nextTick(() => {
        apiKeyInputEl.value?.focus();
    });
};

const saveApiKey = async () => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return;
    const key = apiKeyInput.value.trim();
    if (!key) return;

    savingApiKey.value = true;
    apiKeyInvalid.value = false;
    try {
        const serviceId = currentService.value.id;
        const hadExistingKey = !!(
            aiApiKeyCache[serviceId]?.[platform] ||
            serviceClientSecrets[serviceId]?.list?.[platform]?.client_secret
        );
        await currentService.value.registerClientSecret({
            id: platform,
            key,
            grp: 1,
            req: hadExistingKey ? "update" : "create",
        });
        await currentService.value.setServiceOption({ ai_agent: platform });

        aiApiKeyCache[serviceId] = {
            ...(aiApiKeyCache[serviceId] || {}),
            [platform]: key,
        };

        // Also persist the key as a private record so we can recover the
        // last value on a fresh session without going through the
        // client-secret store.
        void saveAiApiKeyPrivately(serviceId, platform, key);

        const pager = serviceClientSecrets[serviceId];
        if (pager) {
            const mapped = {
                name: platform,
                client_secret: key,
                locked: true,
            };
            if (pager.list?.[platform]) {
                await pager.editItem(mapped);
            } else {
                await pager.insertItems([mapped]);
            }
        }

        apiKeyModalOpen.value = false;
        apiKeyInput.value = "";

        // Drop any stale (likely-empty, due to a prior invalid key) model
        // cache for this platform so the next loadAvailableModels() actually
        // refetches against the new key instead of short-circuiting.
        delete aiModelCache[platform];

        // Re-run the gate so chatEnabled flips on and history loads.
        initialGateLoading.value = true;
        await refreshGate();
        // Force a fresh model fetch with the new key. refreshGate's watcher
        // only fires loadAvailableModels when chatEnabled flips; if the key
        // was previously invalid (chatEnabled was already true) the watcher
        // is a no-op, so kick it off explicitly.
        void loadAvailableModels();
    } catch (err) {
        console.error("Failed to save API key", err);
    } finally {
        savingApiKey.value = false;
    }
};

const CONTEXT_WINDOW_DEFAULT: Record<"claude" | "openai", number> = {
    claude: 200000,
    openai: 128000,
};

const CONTEXT_WINDOW_BY_MODEL: Record<string, number> = {
    "claude-opus-4-7": 200000,
    "claude-sonnet-4": 200000,
    "gpt-5.4": 128000,
};

const OUTPUT_TOKEN_RESERVE = 22000;
const TOOL_AND_RESPONSE_BUFFER = 4000;
const MIN_INPUT_TOKEN_BUDGET = 8000;

// Anthropic's default tier enforces 30,000 input tokens per MINUTE on Opus.
// Cap per-request Claude input below that so a web_fetch result or tool
// response on the follow-up call still fits under the ITPM ceiling. Prompt
// caching (see ai_agent.ts applyHistoryCacheBreakpoint) bills cached prefix
// tokens at ~10% of the rate, which leaves headroom for tool follow-ups.
const CLAUDE_PER_REQUEST_INPUT_CAP = 28000;

const estimateTextTokens = (text: string) => {
    // Approximation: ~4 chars per token for English-like text.
    return Math.ceil((text || "").length / 3);
};

const estimateMessageTokens = (msg: { role: "user" | "assistant"; content: string }) => {
    // Includes lightweight per-message framing overhead.
    return estimateTextTokens(msg.content) + estimateTextTokens(msg.role) + 6;
};

const getContextWindow = (platform: "claude" | "openai", model?: string) => {
    const normalized = (model || "").trim().toLowerCase();
    if (normalized && CONTEXT_WINDOW_BY_MODEL[normalized]) {
        return CONTEXT_WINDOW_BY_MODEL[normalized];
    }
    return CONTEXT_WINDOW_DEFAULT[platform];
};

// Cap how many prior messages we ever send back. The Messages API is stateless
// so every turn re-bills the full history as input tokens; bounding this hard
// keeps us under Anthropic's per-minute input limit regardless of content size.
const MAX_HISTORY_MESSAGES = 20;

// Hard cap on tokens spent on prior history (everything except the latest user
// turn). The latest message always goes through in full so a freshly-attached
// file or pasted context isn't truncated. Sized to comfortably hold ~2 medium
// attachments worth of prior context without re-billing huge bodies forever.
const HISTORY_TOKEN_BUDGET = 8000;

// --- Attachment token-budget estimation ----------------------------------
// We can't know exactly how many tokens an attachment will cost without
// actually fetching and tokenizing what `web_fetch` returns, so apply a
// per-file-type heuristic:
//   - Text/code/markup files: ~3 bytes per token (matches estimateTextTokens).
//   - PDFs: ~5 bytes per token of *extracted* text (PDFs compress structure,
//     extracted text is usually much smaller than the file).
//   - Images: a flat per-image cost approximating Claude's normal-resolution
//     image-token charge (file size doesn't matter; pixel count does, and
//     we can't read pixel dimensions cheaply from a File).
//   - Other binaries / unknown: not counted (web_fetch likely won't return
//     useful tokens for them).
const ESTIMATED_BYTES_PER_TOKEN = 3;
const ESTIMATED_PDF_BYTES_PER_TOKEN = 5000;
const ESTIMATED_IMAGE_TOKENS = 800;

const TEXTLIKE_EXTENSION_RE =
    /\.(txt|md|markdown|rst|csv|tsv|json|jsonl|ndjson|ya?ml|xml|html?|css|less|scss|sass|js|mjs|cjs|ts|tsx|jsx|vue|svelte|astro|py|rb|go|rs|java|kt|swift|c|h|hpp|cpp|cc|cs|php|sh|bash|zsh|ps1|sql|log|conf|cfg|ini|toml|env|gitignore|dockerfile|makefile|lock)$/i;
const PDF_EXTENSION_RE = /\.pdf$/i;
const IMAGE_EXTENSION_RE = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic|heif|avif|svg)$/i;

const estimateFileTokenCost = (file: File): number => {
    const name = file.name || "";
    const size = file.size || 0;
    const type = (file.type || "").toLowerCase();

    if (TEXTLIKE_EXTENSION_RE.test(name) || type.startsWith("text/") || type.includes("json") || type.includes("xml")) {
        return Math.ceil(size / ESTIMATED_BYTES_PER_TOKEN);
    }
    if (PDF_EXTENSION_RE.test(name) || type === "application/pdf") {
        return Math.ceil(size / ESTIMATED_PDF_BYTES_PER_TOKEN);
    }
    if (IMAGE_EXTENSION_RE.test(name) || type.startsWith("image/")) {
        return ESTIMATED_IMAGE_TOKENS;
    }
    // Unknown / opaque binary: don't count toward the budget. The model
    // probably can't read it via web_fetch anyway.
    return 0;
};

const estimateAttachmentTokenCost = (a: AgentAttachment): number => {
    if (a.kind === "file") return estimateFileTokenCost(a.file);
    let total = 0;
    for (const entry of a.files) total += estimateFileTokenCost(entry.file);
    return total;
};

const attachmentsTokenEstimate = computed(() => {
    let total = 0;
    for (const a of attachments.value) {
        total += estimateAttachmentTokenCost(a);
    }
    return total;
});

// Rough per-request input-token budget for the current platform/model. Mirrors
// the math in `buildBoundedChatMessages`: context window minus the output
// reserve and tool buffer, then capped by the Claude per-minute ceiling.
const currentInputTokenBudget = computed(() => {
    const platform = selectedAiPlatform.value;
    if (platform !== "claude" && platform !== "openai") return 0;

    const contextWindow = getContextWindow(platform, selectedAiModel.value);
    const contextBased = Math.max(
        MIN_INPUT_TOKEN_BUDGET,
        contextWindow - OUTPUT_TOKEN_RESERVE - TOOL_AND_RESPONSE_BUFFER,
    );
    return platform === "claude"
        ? Math.min(contextBased, CLAUDE_PER_REQUEST_INPUT_CAP)
        : contextBased;
});

const attachmentsExceedBudget = computed(() => {
    const budget = currentInputTokenBudget.value;
    if (!budget) return false;
    return attachmentsTokenEstimate.value > budget;
});

const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000) {
        const k = tokens / 1000;
        return `${k >= 10 ? Math.round(k) : k.toFixed(1)}k`;
    }
    return String(tokens);
};

const attachmentsBudgetWarning = computed(() => {
    if (!attachmentsExceedBudget.value) return "";
    const est = formatTokenCount(attachmentsTokenEstimate.value);
    const cap = formatTokenCount(currentInputTokenBudget.value);
    return `Attachments are ~${est} tokens, which may exceed the ~${cap}-token per-request limit. Go to "Files" page to upload more files.`;
});

// Replace fenced file blocks from *older* assistant messages with a short
// reference line. A generated CSV/JSON in history can easily be thousands of
// tokens that get re-billed on every subsequent request even though the model
// rarely needs the full body again.
const stripFileBlocksFromHistory = (content: string): string => {
    if (!content) return content;
    return content.replace(
        /```([\w.-]+\.[a-zA-Z0-9]+)\n[\s\S]*?```/g,
        "[file previously attached: $1]",
    );
};

const EXPIRED_ATTACHMENT_URL_HOST = "_expired_.url";
const EXPIRED_ATTACHMENT_URL_ORIGIN = `https://${EXPIRED_ATTACHMENT_URL_HOST}`;
// Regex groups (in order):
//   1: `src::<token>` — model-emitted reference to a db file (path or URL).
//   2: markdown link label where the href IS an http(s) URL
//   3: markdown link href (URL)
//   4: markdown link label where the href is NOT a URL (a storage path)
//   5: markdown link path (non-URL) — treated like `src::<path>`
//   6: bare http(s) URL
const createInlineLinkRegex = () =>
    /src::(\S+)|\[([^\]\n]+)\]\((https?:\/\/(?:[^\s()]+|\([^\s()]*\))+)\)|\[([^\]\n]+)\]\(((?:[^()\n]+|\([^()\n]*\))+)\)|(https?:\/\/[^\s<>"']+)/g;

const safeDecodeURIComponent = (value: string) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const encodePathSegments = (path: string) =>
    path
        .split("/")
        .filter(Boolean)
        .map((s) => encodeURIComponent(s))
        .join("/");

const normalizeAttachmentPathCandidate = (value: string) => {
    return safeDecodeURIComponent((value || "").trim())
        .replace(/\\/g, "/")
        .replace(/^\/+/, "")
        .replace(/\/+/g, "/");
};

const isExpiredAttachmentHref = (href: string) => {
    try {
        return new URL(href).hostname === EXPIRED_ATTACHMENT_URL_HOST;
    } catch {
        return false;
    }
};

const extractRemotePathFromAttachmentHref = (href: string): string | null => {
    try {
        const parsed = new URL(href);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return null;
        }

        // CDN URLs look like: /<serviceId>/<hash>/<hash>/<realPath...>
        // We only want <realPath>. Strip leading segments that match the
        // current service id or look like a 64-char hex hash; everything
        // after that is the real db storage key (filename or folder/filename).
        const path = normalizeAttachmentPathCandidate(parsed.pathname || "");
        const segs = path.split("/").filter(Boolean);
        if (!segs.length) return null;

        const HEX_HASH_RE = /^[a-f0-9]{32,}$/i;
        const serviceId = currentService.value?.id || "";
        let start = 0;
        while (start < segs.length) {
            const seg = segs[start];
            if (seg === serviceId || HEX_HASH_RE.test(seg)) {
                start++;
                continue;
            }
            break;
        }

        const real = segs.slice(start).join("/");
        return real || null;
    } catch {
        return null;
    }
};

const getExpiredAttachmentVisiblePath = (remotePath: string, fallback = "file") => {
    // Keep the full normalized path (folder/filename) so the rendered label
    // and the _expired_.url href both preserve the directory the model wrote.
    // Truncating to the basename here used to corrupt anything not at the
    // bucket root and broke link refresh when remotePath had to be re-derived
    // from the expired href.
    const normalizedPath = normalizeAttachmentPathCandidate(remotePath);
    if (normalizedPath) return normalizedPath;
    return normalizeAttachmentPathCandidate(fallback) || "file";
};

const buildDisplayExpiredAttachmentHref = (remotePath: string, fallback = "file") => {
    const visiblePath = getExpiredAttachmentVisiblePath(remotePath, fallback);
    return `${EXPIRED_ATTACHMENT_URL_ORIGIN}/${encodePathSegments(visiblePath)}`;
};

const sanitizeAttachmentLinksForHistory = (content: string): string => {
    if (!content || !content.includes("Attached files:")) {
        return content;
    }

    return content.replace(
        /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
        (_match, label: string, href: string) => {
            const remotePath = extractRemotePathFromAttachmentHref(href);
            const labelPath = normalizeAttachmentPathCandidate(label);
            const fullPath = remotePath || labelPath;
            if (!fullPath) return `[${label}](${EXPIRED_ATTACHMENT_URL_ORIGIN}/file)`;
            return `[${label}](${buildDisplayExpiredAttachmentHref(fullPath, label)})`;
        },
    );
};

const buildBoundedChatMessages = (options: {
    platform: "claude" | "openai";
    model?: string;
    systemPrompt: string;
    history: ChatMessage[];
}) => {
    const contextWindow = getContextWindow(options.platform, options.model);
    const contextBasedBudget = Math.max(
        MIN_INPUT_TOKEN_BUDGET,
        contextWindow - OUTPUT_TOKEN_RESERVE - TOOL_AND_RESPONSE_BUFFER,
    );
    // On Claude, the per-minute input token rate limit is the real ceiling,
    // not the context window. Cap the per-request input well below the ITPM
    // so web_fetch results and MCP tool responses still fit on follow-up calls.
    const availableInputBudget =
        options.platform === "claude"
            ? Math.min(contextBasedBudget, CLAUDE_PER_REQUEST_INPUT_CAP)
            : contextBasedBudget;

    // For OpenAI the system prompt is part of input, for Claude it's sent separately,
    // but reserving budget for both keeps behavior stable and conservative.
    const systemCost = estimateTextTokens(options.systemPrompt) + 12;
    // Cap older history at HISTORY_TOKEN_BUDGET so big past attachments don't
    // re-bill on every turn. The latest message is still kept in full below.
    const budgetForHistory = Math.max(
        1000,
        Math.min(HISTORY_TOKEN_BUDGET, availableInputBudget - systemCost),
    );

    // Step 1: hard last-N cap before anything else.
    const windowed = options.history.slice(-MAX_HISTORY_MESSAGES);
    const latestIndex = windowed.length - 1;

    // Step 2: strip big file blocks from all but the latest message so the
    // user's current turn still carries any file they just pasted.
    const trimmed: ChatMessage[] = windowed.map((m, i) => {
        if (i === latestIndex) {
            return m;
        }

        const stripped = stripFileBlocksFromHistory(m.content);
        const sanitized =
            m.role === "user"
                ? sanitizeAttachmentLinksForHistory(stripped)
                : stripped;

        return { ...m, content: sanitized };
    });

    // Step 3: token-budget fit from newest to oldest.
    const bounded: ChatMessage[] = [];
    let used = 0;

    for (let i = trimmed.length - 1; i >= 0; i--) {
        const msg = trimmed[i];
        const cost = estimateMessageTokens(msg);
        const wouldOverflow = used + cost > budgetForHistory;

        // Always keep at least the latest message to avoid empty context.
        if (wouldOverflow && bounded.length > 0) {
            break;
        }

        bounded.unshift(msg);
        used += cost;
    }

    return {
        messages: bounded.map((m) => ({ role: m.role, content: m.content })),
        droppedCount: Math.max(0, options.history.length - bounded.length),
        estimatedInputTokens: used + systemCost,
        estimatedBudget: availableInputBudget,
    };
};

const scrollToBottom = async (smooth = false) => {
    await nextTick();
    // Wait two animation frames so layout settles (CSS var height changes,
    // image loads, on-screen keyboard) before we compute scrollHeight. On
    // mobile the --agent-available-height CSS var is applied asynchronously
    // via rAF, so a single nextTick is not enough - the first scrollHeight
    // read can be stale and leave us short of the real bottom.
    await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    if (messagesBox.value) {
        stickToBottom = true;
        if (smooth) {
            messagesBox.value.scrollTo({
                top: messagesBox.value.scrollHeight,
                behavior: "smooth",
            });
            return;
        }

        messagesBox.value.scrollTop = messagesBox.value.scrollHeight;
    }
};

// Whether the messages view is currently pinned to the bottom. Toggled by
// scrollToBottom (true) and by the user scrolling away (false). Used to
// re-pin after viewport height changes so a late --agent-available-height
// update doesn't leave the view short of the bottom on mobile.
let stickToBottom = true;

const autoGrowInput = () => {
    if (!inputBox.value) {
        return;
    }

    const minHeight = parseFloat(window.getComputedStyle(inputBox.value).minHeight) || 0;
    inputBox.value.style.height = "auto";
    inputBox.value.style.height = `${Math.max(inputBox.value.scrollHeight, minHeight)}px`;
};

const handlePromptCompositionEnd = async () => {
    isPromptComposing.value = false;
    await nextTick();
    autoGrowInput();
};

const handlePromptEnter = async (event: KeyboardEvent) => {
    if (event.isComposing || isPromptComposing.value || event.keyCode === 229) {
        return;
    }

    event.preventDefault();
    await nextTick();
    await sendMessage();
};

const getErrorMessage = (err: any) => {
    if (typeof err === "string") {
        return err;
    }

    if (err?.body?.error?.message && typeof err.body.error.message === "string") {
        return err.body.error.message;
    }

    if (err?.error?.message && typeof err.error.message === "string") {
        return err.error.message;
    }

    if (err?.message && typeof err.message === "string") {
        return err.message;
    }

    return "Request failed. Please try again.";
};

// True for any AI-provider response that should be surfaced as a red error
// bubble. Handles:
//   - bare shape `{type:'error', error:{...}}`
//   - proxy-wrapped shape `{body:{type:'error', error:{...}}, status_code:4xx}`
//   - AWS-style error JSON like `{message: "An error occurred (ValidationException) ..."}`
//     which the proxy returns as the response body when the upstream call fails
//     before any provider response can be produced. Such a body has no Claude
//     `content` array and no OpenAI `output` / `output_text` / `choices`.
const isErrorResponseBody = (response: any): boolean => {
    if (!response || typeof response !== "object") return false;
    if (typeof response.status_code === "number" && response.status_code >= 400) return true;
    if (response.type === "error") return true;
    if (response.error && (response.error.message || response.error.type)) return true;
    const body = response.body;
    if (body && typeof body === "object") {
        if (body.type === "error") return true;
        if (body.error && (body.error.message || body.error.type)) return true;
    }
    // AWS/Lambda-style `{message: "..."}` with no recognizable success payload.
    if (typeof response.message === "string" && response.message.length) {
        const hasClaudeBody = Array.isArray(response.content);
        const hasOpenAIBody =
            typeof response.output_text === "string" ||
            Array.isArray(response.output) ||
            Array.isArray(response.choices);
        if (!hasClaudeBody && !hasOpenAIBody) return true;
    }
    return false;
};

// Detect MCP / upstream "your access token is no longer valid" failures so we
// can transparently force-refresh the skapi session and retry once. The
// `$ACCESS_TOKEN` placeholder in clientSecretRequest is substituted with the
// caller's skapi access token, which the MCP server validates via skapi-js's
// `loginWithToken`. When a queued request is processed after the captured
// token has expired (long poll, retry, etc.) the MCP call surfaces here as
// "Token has expired" / 401 / invalid_token.
const isAuthExpiredError = (input: any): boolean => {
    if (!input) return false;
    const blobs: string[] = [];
    const push = (v: any) => { if (typeof v === "string" && v) blobs.push(v); };
    if (typeof input === "string") push(input);
    else {
        push(input.message);
        push(input.code);
        if (input.error) {
            push(input.error.message);
            push(input.error.code);
            push(input.error.type);
        }
        if (input.body) {
            push(input.body.message);
            if (input.body.error) {
                push(input.body.error.message);
                push(input.body.error.code);
                push(input.body.error.type);
            }
        }
        if (typeof input.status === "number" && input.status === 401) return true;
        if (typeof input.status_code === "number" && input.status_code === 401) return true;
    }
    const hay = blobs.join(" | ").toLowerCase();
    if (!hay) return false;
    return (
        hay.includes("token has expired") ||
        hay.includes("token is expired") ||
        hay.includes("expired_token") ||
        hay.includes("invalid_token") ||
        hay.includes("unauthorized") ||
        hay.includes("not authorized") ||
        (hay.includes("invalid_request") && hay.includes("token"))
    );
};

// Force the skapi session to refresh its access/id tokens, so the next
// `clientSecretRequest` (which substitutes `$ACCESS_TOKEN` from the live
// session) carries a fresh JWT. `getProfile({ refreshToken: true })` is the
// public skapi-js entrypoint that performs the Cognito refresh.
const refreshSkapiSession = async (): Promise<boolean> => {
    try {
        await (skapi as any).getProfile({ refreshToken: true });
        return true;
    } catch (err) {
        console.warn("[agent] refreshSkapiSession failed", err);
        return false;
    }
};

const debugAgentLoad = (
    stage: string,
    details: Record<string, unknown> = {}
) => {
    return; // comment out to enable debug logs
    console.debug("[agent]", stage, details);
};

const clearChatState = () => {
    messages.value = [];
    historyEndOfList.value = false;
    historyStartKeyHistory.value = [];
    cancelPendingHistoryPoll();
};

// "Clear chat history" is a soft delete: we persist a `clearedAt` timestamp
// per (service, platform) in localStorage and filter any history entries with
// `updated <= clearedAt` out of fetched results. The server still has the
// records (skapi has no public delete API for clientSecretRequest history),
// but the UI and the model never see them again.
const CLEAR_HISTORY_STORAGE_PREFIX = "agent.clearedAt";

const showClearHistoryModal = ref(false);
const clearingHistory = ref(false);

const getClearHistoryStorageKey = () => {
    const id = currentService.value?.id || "";
    if (!id || selectedAiPlatform.value === "none") return "";
    return `${CLEAR_HISTORY_STORAGE_PREFIX}:${id}#${selectedAiPlatform.value}`;
};

const getClearedAt = (): number => {
    const key = getClearHistoryStorageKey();
    if (!key) return 0;
    try {
        const raw = window.localStorage.getItem(key);
        const value = raw ? Number(raw) : 0;
        return Number.isFinite(value) && value > 0 ? value : 0;
    } catch {
        return 0;
    }
};

const setClearedAt = (timestamp: number) => {
    const key = getClearHistoryStorageKey();
    if (!key) return;
    try {
        window.localStorage.setItem(key, String(timestamp));
    } catch {
        /* localStorage may be unavailable (Safari private mode); ignore */
    }
};

// PollingResult.updated is a millisecond timestamp on each history entry. We
// only keep entries strictly newer than the horizon so a clear performed at
// time T also drops messages saved at T (within the same ms).
const filterListByClearHorizon = (list: any[]): any[] => {
    const clearedAt = getClearedAt();
    if (!clearedAt) return list;
    return list.filter((item) => {
        const updated = Number(item?.updated);
        return Number.isFinite(updated) && updated > clearedAt;
    });
};

const onClearHistoryClick = () => {
    if (!chatEnabled.value || sending.value || typing.value || uploadingAttachments.value) {
        return;
    }
    if (!messages.value.length) return;
    showClearHistoryModal.value = true;
};

const confirmClearHistory = async () => {
    if (clearingHistory.value) return;
    clearingHistory.value = true;
    try {
        // Use the most recent visible message timestamp if we can derive one,
        // otherwise just `Date.now()`. Date.now() is fine because new messages
        // sent *after* this point will have a later `updated` value.
        setClearedAt(Date.now());

        // Wipe any in-memory cache so navigating away and back doesn't restore
        // the cleared messages from the per-tab cache.
        const cacheKey = getHistoryCacheKey();
        if (cacheKey) {
            delete aiChatHistoryCache[cacheKey];
        }

        messages.value = [];
        historyStartKeyHistory.value = [];
        // No older pages can ever pass the horizon, so we're at end-of-list.
        historyEndOfList.value = true;

        showClearHistoryModal.value = false;
    } finally {
        clearingHistory.value = false;
    }
};

const getHistoryCacheKey = () => {
    return `${currentService.value?.id || ""}#${selectedAiPlatform.value}`;
};

const normalizeTextContent = (content: any): string => {
    if (typeof content === "string") {
        return content;
    }

    if (Array.isArray(content)) {
        return content
            .map((part: any) => {
                if (typeof part === "string") {
                    return part;
                }

                if (part?.type === "text" || part?.type === "input_text" || part?.type === "output_text") {
                    return part?.text || "";
                }

                return "";
            })
            .join("\n")
            .trim();
    }

    return "";
};

const extractLastUserTextFromRequest = (requestBody: any) => {
    const messageArray = Array.isArray(requestBody?.messages)
        ? requestBody.messages
        : Array.isArray(requestBody?.input)
            ? requestBody.input
            : [];

    for (let i = messageArray.length - 1; i >= 0; i--) {
        const item = messageArray[i];
        if (item?.role === "user") {
            const text = normalizeTextContent(item?.content);
            if (text) {
                return text;
            }
        }
    }

    return "";
};

// Items tagged with _isBgTask come from the background-indexing queue.
type TaggedHistoryItem = any & { _isBgTask?: boolean };

const mapHistoryListToMessages = (list: TaggedHistoryItem[], platform: "claude" | "openai") => {
    const mapped: ChatMessage[] = [];
    const extractAssistantText = platform === "openai" ? extractOpenAIText : extractClaudeText;
    const filtered = filterListByClearHorizon(list);

    // The list is descending (newest first). Requests are processed in FIFO
    // order per queue. Track the oldest pending item separately for the
    // bg-indexing queue and the chat queue so each can show its own
    // "(In process)" placeholder simultaneously (the queues run in parallel).
    let oldestPendingChatItem: any = null;
    let oldestPendingBgItem: any = null;
    for (let i = filtered.length - 1; i >= 0; i--) {
        const it = filtered[i];
        if (it?.status === "pending" || it?.status === "running") {
            if (it._isBgTask) {
                if (!oldestPendingBgItem) oldestPendingBgItem = it;
            } else {
                if (!oldestPendingChatItem) oldestPendingChatItem = it;
            }
        }
    }

    for (const item of [...filtered].reverse()) {
        // skapi.clientSecretRequestHistory remaps the raw entry into a
        // PollingResult: { request_body, response_body, error, status,
        // status_code, updated, expires }. When the upstream call to the AI
        // provider failed (e.g. 400 "prompt is too long"), the body lives in
        // `error`, NOT `response_body`, and `status === 'failed'`.
        const requestBody = item?.request_body;
        // The backend worker transitions items from "pending" → "running" while
        // actively processing them. The list endpoint returns the raw status, so
        // "running" must be treated the same as "pending" here. (The single-item
        // polling path already normalises "running" to "pending" server-side, but
        // the list path does not.)
        const isPending = item?.status === "pending" || item?.status === "running";
        const isFailed = item?.status === "failed";
        const response = isFailed
            ? (item?.error ?? item?.response_body)
            : (item?.response_body ?? item?.error);
        const userText = extractLastUserTextFromRequest(requestBody);
        const assistantText = isPending
            ? ""
            : extractAssistantText(response)?.trim() || "";
        const isErrorResponse = !isPending && (isFailed || isErrorResponseBody(response));
        const oldestPendingItem = item._isBgTask ? oldestPendingBgItem : oldestPendingChatItem;
        const isInProcess = isPending && item === oldestPendingItem;
        const isQueued = isPending && !isInProcess;
        const serverItemId = typeof item?.id === "string" && item.id
            ? item.id
            : undefined;

        if (userText) {
            const userMsg: ChatMessage = {
                role: "user",
                content: sanitizeAttachmentLinksForHistory(userText),
            };
            if (isInProcess) userMsg.isPendingInProcess = true;
            if (isQueued) userMsg.isPendingQueued = true;
            if (item._isBgTask) userMsg.isBackgroundTask = true;
            if (serverItemId !== undefined) userMsg._serverItemId = serverItemId;
            mapped.push(userMsg);
        }

        if (isInProcess) {
            const placeholder: ChatMessage = {
                role: "assistant",
                content: "",
                isPending: true,
                isPendingInProcess: true,
            };
            if (item._isBgTask) placeholder.isBackgroundTask = true;
            if (serverItemId !== undefined) placeholder._serverItemId = serverItemId;
            mapped.push(placeholder);
        } else if (isQueued) {
            // No assistant placeholder for queued items — the user-message
            // bubble alone (with "(In queue)" small label) communicates state.
        } else if (isErrorResponse) {
            const errMsg: ChatMessage = {
                role: "assistant",
                content: getErrorMessage(response),
                isError: true,
            };
            if (item._isBgTask) errMsg.isBackgroundTask = true;
            if (serverItemId !== undefined) errMsg._serverItemId = serverItemId;
            mapped.push(errMsg);
        } else if (assistantText) {
            const okMsg: ChatMessage = { role: "assistant", content: assistantText };
            if (item._isBgTask) okMsg.isBackgroundTask = true;
            if (serverItemId !== undefined) okMsg._serverItemId = serverItemId;
            mapped.push(okMsg);
        }
    }

    return mapped;
};

const updateHistoryCache = () => {
    const key = getHistoryCacheKey();
    if (!key || selectedAiPlatform.value === "none") {
        return;
    }

    aiChatHistoryCache[key] = {
        messages: [...messages.value],
        endOfList: historyEndOfList.value,
        startKeyHistory: [...historyStartKeyHistory.value],
    };
};

const cancelPendingHistoryPoll = () => {
    if (pendingPollTimer !== null) {
        clearTimeout(pendingPollTimer);
        pendingPollTimer = null;
    }
};

// While any message is still pending on the server (status === 'pending' from
// clientSecretRequestHistory), keep refetching the first page so the user
// sees the response land as soon as the upstream provider returns it.
const schedulePendingHistoryRefetch = (token: number) => {
    cancelPendingHistoryPoll();

    if (agentUnmounted || token !== gateRefreshToken) {
        return;
    }

    const stillPending = messages.value.some((m) => m.isPending);
    if (!stillPending) {
        return;
    }

    pendingPollTimer = setTimeout(() => {
        pendingPollTimer = null;
        if (agentUnmounted || token !== gateRefreshToken) {
            return;
        }
        if (!chatEnabled.value || selectedAiPlatform.value === "none") {
            return;
        }
        // Only refetch if no other history request is currently in flight.
        if (loadingHistory.value) {
            schedulePendingHistoryRefetch(token);
            return;
        }
        void fetchHistoryPage(false, token, true);
    }, PENDING_POLL_INTERVAL_MS);
};

const fetchHistoryPage = async (
    fetchMore = false,
    token = gateRefreshToken,
    pollMode = false
) => {
    if (
        (loadingHistory.value && historyRequestToken === token) ||
        !chatEnabled.value ||
        selectedAiPlatform.value === "none" ||
        !currentService.value
    ) {
        return;
    }

    // In pollMode, the sole purpose is to update pending-item status. But if
    // sendMessage is actively dispatching a chat request, it has already pushed
    // the user message locally and is polling for its own response via
    // callClaudeWithPublicMcp. Replacing messages.value here would cause the
    // locally-pushed message to transiently show yellow queue-styling (because
    // the server sees it as isPendingInProcess) and then "disappear" before
    // typewriteLatestReply restores it. Reschedule so bg tasks resume updating
    // as soon as the send/typewrite flow completes.
    if (pollMode && (sending.value || typing.value)) {
        schedulePendingHistoryRefetch(token);
        return;
    }

    historyRequestToken = token;
    loadingHistory.value = true;
    if (fetchMore) {
        loadingOlderHistory.value = true;
    }
    const prevScrollHeight = messagesBox.value?.scrollHeight || 0;
    const prevScrollTop = messagesBox.value?.scrollTop || 0;
    // Snapshot which messages were pending before this refresh keyed by their
    // server item id (item.updated). After refresh we use this set to detect
    // pending→resolved transitions in poll mode and scroll the resolved
    // bubble into view, instead of jumping the user's scroll to the bottom.
    const prevPendingServerIds = new Set<string>(
        messages.value
            .filter((m) => m.isPending && typeof m._serverItemId === "string")
            .map((m) => m._serverItemId as string)
    );
    const platform = selectedAiPlatform.value as "claude" | "openai";
    let serviceId = currentService.value.id;
    let owner = currentService.value.owner;

    // Re-resolve the active project before dispatch to guard against stale
    // in-memory context (e.g. account switched in another tab). Only do this
    // on the *initial* first-page load — `fetchMore` (scroll-up pagination)
    // and `pollMode` (background pending-request poll) reuse the project
    // context the first-page fetch already validated. Re-running it on every
    // scroll event was wasteful, raced with itself, and — critically — its
    // early-return branches never reset `loadingHistory` / `loadingOlderHistory`,
    // so a single re-resolution miss would permanently block pagination.
    if (!fetchMore && !pollMode) {
        const clearLoadingFlags = () => {
            if (historyRequestToken === token) {
                loadingHistory.value = false;
                loadingOlderHistory.value = false;
            }
        };
        try {
            const resolved = await pullServices({ service_id_to_fetch: serviceId });
            if (!resolved) {
                messages.value.push({
                    role: "assistant",
                    content: "Project is no longer reachable in your current session. Please re-open the project from My Projects and try again.",
                    isError: true,
                });
                updateHistoryCache();
                sending.value = false;
                clearLoadingFlags();
                return;
            }
            currentService.value = resolved;
            serviceId = currentService.value.id;
            owner = currentService.value.owner;
            if (!serviceId || !owner) {
                messages.value.push({
                    role: "assistant",
                    content: "Project context is invalid in this session. Please reopen the project and try again.",
                    isError: true,
                });
                updateHistoryCache();
                sending.value = false;
                clearLoadingFlags();
                return;
            }
        } catch (_resolveErr) {
            messages.value.push({
                role: "assistant",
                content: "Failed to validate current project access before querying data. Please refresh and try again.",
                isError: true,
            });
            updateHistoryCache();
            sending.value = false;
            clearLoadingFlags();
            return;
        }
    }

    try {
        const options: Record<string, any> = { fetchMore };

        if (fetchMore && historyStartKeyHistory.value.length) {
            options.startKeyHistory = [...historyStartKeyHistory.value];
        }

        debugAgentLoad("getChatHistory start", {
            token,
            fetchMore,
            serviceId,
            platform,
        });

        const fetchHistory = () => getChatHistory(
            {
                service: serviceId,
                owner,
                platform,
            },
            options
        );

        // On first-page loads fetch the bg-indexing queue in parallel so both
        // queues are shown together. Pagination (fetchMore) only applies to the
        // main chat queue; bg tasks are recent by nature and always first-page.
        const fetchBgHistory = () => getChatHistory(
            {
                service: serviceId,
                owner,
                platform,
                queue: serviceId + BG_INDEXING_QUEUE_SUFFIX,
            },
            { fetchMore: false }
        );

        let history: any;
        let bgHistory: any = null;
        try {
            if (!fetchMore) {
                [history, bgHistory] = await Promise.all([
                    fetchHistory(),
                    fetchBgHistory().catch(() => null),
                ]);
            } else {
                history = await fetchHistory();
            }
        } catch (err: any) {
            if (isAuthExpiredError(err)) {
                await refreshSkapiSession();
                if (!fetchMore) {
                    [history, bgHistory] = await Promise.all([
                        fetchHistory(),
                        fetchBgHistory().catch(() => null),
                    ]);
                } else {
                    history = await fetchHistory();
                }
            } else {
                throw err;
            }
        }

        debugAgentLoad("getChatHistory resolved", {
            token,
            fetchMore,
            serviceId,
            platform,
            count: Array.isArray(history?.list) ? history.list.length : 0,
        });

        // console.log("chat history fetched", history);

        if (token !== gateRefreshToken) {
            return;
        }

        const chatList: any[] = Array.isArray(history?.list) ? history.list : [];
        const bgList: any[] = (!fetchMore && Array.isArray(bgHistory?.list)) ? bgHistory.list : [];

        // Tag bg items so mapHistoryListToMessages can distinguish them, then
        // merge both lists sorted by item id descending (id encodes creation
        // timestamp as a lexicographically-sortable base32hex stamp).
        const taggedBgList = bgList.map((item: any) => ({ ...item, _isBgTask: true }));
        const list = chatList.concat(taggedBgList).sort((a: any, b: any) => {
            const ai = typeof a.id === "string" ? a.id : "";
            const bi = typeof b.id === "string" ? b.id : "";
            if (ai > bi) return -1;
            if (ai < bi) return 1;
            return 0;
        });

        const mapped = mapHistoryListToMessages(list, platform);

        if (fetchMore) {
            messages.value = [...mapped, ...messages.value];
        } else {
            messages.value = mapped;
            // First-page fetch consumed the latest external refresh signal
            // for this service. Sync the last-seen counter so subsequent
            // watcher callbacks won't re-fire for a bump we already covered.
            const sid = currentService.value?.id;
            if (sid) {
                const cur = agentHistoryRefreshTrigger[sid];
                if (typeof cur === "number") {
                    agentHistoryRefreshLastSeen[sid] = cur;
                }
            }
        }

        historyEndOfList.value = !!history?.endOfList;
        historyStartKeyHistory.value = Array.isArray(history?.startKeyHistory)
            ? history.startKeyHistory
            : [];

        // If the cleared-history horizon dropped any item from this page, every
        // older page is also pre-horizon (history is descending), so we can stop
        // fetching older pages. Use chatList — it drives pagination; bgList is
        // first-page only and must not influence the end-of-list signal.
        const clearedAt = getClearedAt();
        if (clearedAt && chatList.length > 0) {
            const oldest = chatList[chatList.length - 1] as any;
            const oldestUpdated = Number(oldest?.updated);
            if (Number.isFinite(oldestUpdated) && oldestUpdated <= clearedAt) {
                historyEndOfList.value = true;
            }
        }

        updateHistoryCache();

        // If any message is still pending on the server, keep refetching
        // until the most-recent (and other) pending requests resolve.
        if (!fetchMore) {
            schedulePendingHistoryRefetch(token);
        }

        // If the server has one or more in-flight chat requests for this
        // conversation (started in another tab, before a refresh, etc.),
        // history.pending is an array of promises. Append a Thinking
        // placeholder for each and replace them in place as they resolve.
        if (!fetchMore && Array.isArray(history?.pending) && history.pending.length) {
            const cacheKey = `${serviceId}#${platform}`;
            attachPendingHistoryRequests(cacheKey, history.pending, platform);
        }

        if (fetchMore && messagesBox.value) {
            await nextTick();
            if (token !== gateRefreshToken || !messagesBox.value) {
                return;
            }
            const nextScrollHeight = messagesBox.value.scrollHeight;
            messagesBox.value.scrollTop = prevScrollTop + (nextScrollHeight - prevScrollHeight);
        }

        if (!fetchMore) {
            if (pollMode) {
                // Interval poll: do NOT auto-scroll to the bottom on every
                // refresh — that would yank the user's scroll position while
                // they're reading. Instead, if a previously-pending request
                // has just resolved, scroll that resolved bubble into view
                // so the user sees the response land. If nothing resolved
                // since the last poll, leave the scroll alone.
                if (prevPendingServerIds.size > 0) {
                    const resolvedIdx = messages.value.findIndex(
                        (m) =>
                            !m.isPending &&
                            !m.isBackgroundTask &&
                            m.role === "assistant" &&
                            typeof m._serverItemId === "string" &&
                            prevPendingServerIds.has(m._serverItemId as string)
                    );
                    if (resolvedIdx >= 0) {
                        await nextTick();
                        if (token === gateRefreshToken && messagesBox.value) {
                            const el = messagesBox.value.querySelector(
                                `.bq-message[data-msg-index="${resolvedIdx}"]`
                            ) as HTMLElement | null;
                            if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        }
                    }
                }
            } else {
                await scrollToBottom();
            }
        }
    } catch (err) {
        if (token === gateRefreshToken) {
            debugAgentLoad("getChatHistory failed", {
                token,
                fetchMore,
                serviceId,
                platform,
                error: getErrorMessage(err),
            });
        }
        // history is optional; ignore if unavailable
    } finally {
        if (historyRequestToken === token) {
            loadingHistory.value = false;
            loadingOlderHistory.value = false;
        }
    }
};

const refreshGate = async () => {
    const token = ++gateRefreshToken;
    const parsedAiAgent = parseAiAgentValue(currentService.value?.service?.ai_agent);

    selectedAiPlatform.value = parsedAiAgent.platform;
    selectedAiModel.value = parsedAiAgent.model;
    hasAiPlatform.value = parsedAiAgent.hasPlatform;
    hasAiApiKey.value = false;
    initialGateLoading.value = parsedAiAgent.hasPlatform;
    let shouldLoadHistory = false;
    let shouldClearHistory = false;

    if (!currentService.value || !hasAiPlatform.value) {
        shouldClearHistory = true;
    } else {
            const serviceId = currentService.value.id;
            const secretId = selectedAiPlatform.value as "claude" | "openai";

            // check lightweight cache first
            if (aiApiKeyCache[serviceId]?.[secretId]) {
                hasAiApiKey.value = true;
                shouldLoadHistory = true;
            } else {
                // check the shared client-secrets pager if it has this key cached already
                const pager = serviceClientSecrets[serviceId];
                if (pager?.list?.[secretId]?.client_secret) {
                    hasAiApiKey.value = true;
                    shouldLoadHistory = true;
                    aiApiKeyCache[serviceId] = {
                        ...(aiApiKeyCache[serviceId] || {}),
                        [secretId]: pager.list[secretId].client_secret,
                    };
                } else {
                    try {
                        debugAgentLoad("listClientSecrets start", {
                            token,
                            serviceId,
                            secretId,
                        });

                        const result = await currentService.value.listClientSecrets(false, secretId);

                        debugAgentLoad("listClientSecrets resolved", {
                            token,
                            serviceId,
                            secretId,
                            count: result?.list?.length || 0,
                        });

                        if (token !== gateRefreshToken) {
                            return;
                        }

                        const match = result?.list?.find((s) => s.id === secretId);

                        if (match?.key && typeof match.key === "string" && match.key.trim()) {
                            hasAiApiKey.value = true;
                            shouldLoadHistory = true;
                            aiApiKeyCache[serviceId] = {
                                ...(aiApiKeyCache[serviceId] || {}),
                                [secretId]: match.key,
                            };
                        } else {
                            shouldClearHistory = true;
                        }

                        debugAgentLoad("listClientSecrets cached", {
                            token,
                            serviceId,
                            secretId,
                            cached: !!aiApiKeyCache[serviceId]?.[secretId],
                        });
                    } catch (err) {
                        if (token === gateRefreshToken) {
                            debugAgentLoad("listClientSecrets failed", {
                                token,
                                serviceId,
                                secretId,
                                error: getErrorMessage(err),
                            });
                        }
                        hasAiApiKey.value = false;
                        shouldClearHistory = true;
                    }
                }
            }
        }

    if (token !== gateRefreshToken) {
        return;
    }

    if (shouldLoadHistory && hasAiApiKey.value && hasAiPlatform.value) {
        await loadChatHistory(token);
        if (token === gateRefreshToken) {
            initialGateLoading.value = false;
            await scrollToBottom(false);
            void resumePendingRequest(token);
        }
        return;
    }

    if (shouldClearHistory) {
        clearChatState();
    }

    if (token === gateRefreshToken) {
        initialGateLoading.value = false;
    }
};

const loadChatHistory = async (token = gateRefreshToken) => {
    if (token !== gateRefreshToken) {
        return;
    }

    if (!hasAiApiKey.value || !hasAiPlatform.value || selectedAiPlatform.value === "none") {
        clearChatState();
        return;
    }

    const cacheKey = getHistoryCacheKey();
    const cached = aiChatHistoryCache[cacheKey];

    if (cached) {
        if (token !== gateRefreshToken) {
            return;
        }
        const sanitizedCachedMessages = cached.messages.map((m) =>
            m.role === "user"
                ? { ...m, content: sanitizeAttachmentLinksForHistory(m.content) }
                : m,
        );
        messages.value = sanitizedCachedMessages;
        historyEndOfList.value = !!cached.endOfList;
        historyStartKeyHistory.value = Array.isArray(cached.startKeyHistory)
            ? [...cached.startKeyHistory]
            : [];
        aiChatHistoryCache[cacheKey] = {
            ...cached,
            messages: sanitizedCachedMessages,
        };
        await scrollToBottom();
        schedulePendingHistoryRefetch(token);

        // If `agentHistoryRefreshTrigger[serviceId]` was bumped while this
        // view was unmounted (e.g. user was on the dbfile tab during a
        // batch upload that enqueued background indexing requests), the
        // in-memory cache we just restored is stale. Do an immediate fresh
        // fetch (pollMode so it respects the send/type guard) to surface any
        // bg-queue entries already registered server-side, then also kick the
        // detect loop for entries that haven't been registered yet.
        const serviceId = currentService.value?.id;
        if (serviceId) {
            const cur = agentHistoryRefreshTrigger[serviceId];
            const last = agentHistoryRefreshLastSeen[serviceId];
            if (typeof cur === "number" && cur !== last) {
                agentHistoryRefreshLastSeen[serviceId] = cur;
                void fetchHistoryPage(false, token, true);
                startQueueDetectPoll();
            }
        }
        return;
    }

    historyEndOfList.value = false;
    historyStartKeyHistory.value = [];
    await fetchHistoryPage(false, token);

    // No-cache path: a fresh fetch may have happened too early to include
    // the queued indexing requests (the proxy registers them
    // asynchronously). If the refresh trigger has been bumped since we
    // last consumed it, kick the queue-detect loop so we keep polling
    // until the new pending entries surface.
    const sidNoCache = currentService.value?.id;
    if (sidNoCache) {
        const cur = agentHistoryRefreshTrigger[sidNoCache];
        const last = agentHistoryRefreshLastSeen[sidNoCache];
        if (typeof cur === "number" && cur !== last) {
            agentHistoryRefreshLastSeen[sidNoCache] = cur;
            startQueueDetectPoll();
        }
    }
};

const fetchOlderHistoryIfNeeded = async () => {
    if (
        loadingHistory.value ||
        !chatEnabled.value ||
        selectedAiPlatform.value === "none" ||
        historyEndOfList.value
    ) {
        return;
    }

    await fetchHistoryPage(true, gateRefreshToken);
};

const onHistoryScroll = async () => {
    if (!messagesBox.value) {
        return;
    }

    const el = messagesBox.value;
    // Track whether the user is pinned to the bottom so a later viewport
    // height change can re-stick if they never scrolled away.
    stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 16;

    if (el.scrollTop <= 60) {
        await fetchOlderHistoryIfNeeded();
    }
};

const applyAgentViewportHeight = () => {
    if (!agentRoot.value) {
        return;
    }

    const topOffset = Math.max(0, agentRoot.value.getBoundingClientRect().top);
    const isDesktop = window.matchMedia("(min-width: 801px)").matches;
    const navHeight = isDesktop ? (document.getElementById("navBar") as HTMLElement | null)?.offsetHeight || 0 : 32;
    const footerHeight = isDesktop
        ? parseFloat(getComputedStyle(document.body).getPropertyValue("--footer-height")) || 0
        : 0;

    const available = Math.max(240, window.innerHeight - topOffset - navHeight - footerHeight);
    agentRoot.value.style.setProperty("--agent-available-height", `${available}px`);

    // Changing the container height reflows the messages box. If the user
    // is pinned to the bottom (e.g. just mounted, or just sent a message),
    // re-scroll so the late height update on mobile doesn't leave us short
    // of the bottom.
    if (stickToBottom && messagesBox.value) {
        requestAnimationFrame(() => {
            if (stickToBottom && messagesBox.value) {
                messagesBox.value.scrollTop = messagesBox.value.scrollHeight;
            }
        });
    }
};

const scheduleAgentViewportHeight = (remainingChecks = 6) => {
    window.cancelAnimationFrame(viewportHeightFrame);

    const run = async () => {
        await nextTick();
        viewportHeightFrame = window.requestAnimationFrame(() => {
            const root = agentRoot.value;

            if (!root) {
                return;
            }

            const isVisible = root.getClientRects().length > 0 && root.getBoundingClientRect().height > 0;

            if (!isVisible && remainingChecks > 0) {
                scheduleAgentViewportHeight(remainingChecks - 1);
                return;
            }

            applyAgentViewportHeight();
        });
    };

    void run();
};

const onWindowResize = () => {
    scheduleAgentViewportHeight();
};

type MsgPart =
    | { type: 'text'; text: string }
    | { type: 'html'; html: string }
    | { type: 'file'; filename: string; href: string }
    | {
          type: 'link';
          label: string;
          fullLabel?: string;
          href: string;
          expired: boolean;
          expiredHref?: string;
          remotePath?: string;
      };

const refreshingLinkMap = ref<Record<string, boolean>>({});
const refreshedExpiredLinkMap = ref<Record<string, string>>({});
const refreshingLinkPromises = new Map<string, Promise<string>>();

// Temporary URLs the backend hands out are bucketed to wall-clock 15-minute
// UTC windows (00, 15, 30, 45). When the window rolls over, every URL minted
// during the previous window stops working at the same instant — so we drop
// our entire fresh-link cache at that boundary and let any visible link
// revert to its `_expired_.url` placeholder. A subsequent click will
// re-resolve it via getTemporaryUrl with a fresh 15-minute lifetime.
const LINK_REFRESH_WINDOW_MS = 15 * 60 * 1000;
let refreshedLinkExpiryTimer: ReturnType<typeof setTimeout> | null = null;

const msUntilNextLinkExpiryBoundary = () => {
    const now = Date.now();
    const nextBoundary = Math.ceil(now / LINK_REFRESH_WINDOW_MS) * LINK_REFRESH_WINDOW_MS;
    // If we're already exactly on a boundary, schedule for the next one so
    // we don't fire a zero-delay timeout that races the URL we just stored.
    return Math.max(1, nextBoundary - now);
};

const expireAllRefreshedLinks = () => {
    if (Object.keys(refreshedExpiredLinkMap.value).length) {
        refreshedExpiredLinkMap.value = {};
    }
};

const scheduleNextLinkExpiryBoundary = () => {
    if (refreshedLinkExpiryTimer) {
        clearTimeout(refreshedLinkExpiryTimer);
        refreshedLinkExpiryTimer = null;
    }
    refreshedLinkExpiryTimer = setTimeout(() => {
        expireAllRefreshedLinks();
        scheduleNextLinkExpiryBoundary();
    }, msUntilNextLinkExpiryBoundary());
};

const ensureLinkExpiryBoundaryScheduled = () => {
    if (!refreshedLinkExpiryTimer) {
        scheduleNextLinkExpiryBoundary();
    }
};

const isLinkRefreshing = (part: MsgPart) =>
    part.type === "link" && !!refreshingLinkMap.value[part.expiredHref || part.href];

// Maximum number of characters of an attachment label to render inline.
// Longer labels are truncated with a leading ellipsis so the END of the path
// (the filename) stays visible: `tokyo/some/very/long/folder/booking.pdf`
// becomes `…ng/folder/booking.pdf`. Full path is preserved on the part as
// `fullLabel` and surfaced via the title attribute / download filename.
const LINK_LABEL_MAX_DISPLAY_CHARS = 32;

const truncateLabelForDisplay = (label: string): string => {
    if (!label) return label;
    if (label.length <= LINK_LABEL_MAX_DISPLAY_CHARS) return label;
    // Reserve 1 char for the leading ellipsis.
    return "\u2026" + label.slice(label.length - (LINK_LABEL_MAX_DISPLAY_CHARS - 1));
};

const normalizeTrailingInlineToken = (value: string): string => {
    if (!value) return value;
    let out = value;

    // Trim sentence punctuation that may trail links in prose.
    out = out.replace(/[.,;:!?]+$/, "");

    // Remove only unmatched closing wrappers, preserving balanced pairs.
    const trimUnmatched = (openCh: string, closeCh: string) => {
        while (out.endsWith(closeCh)) {
            const openCount = (out.match(new RegExp(`\\${openCh}`, "g")) || []).length;
            const closeCount = (out.match(new RegExp(`\\${closeCh}`, "g")) || []).length;
            if (closeCount > openCount) {
                out = out.slice(0, -1);
            } else {
                break;
            }
        }
    };

    trimUnmatched("(", ")");
    trimUnmatched("[", "]");
    trimUnmatched("{", "}");

    // Common markdown wrappers that can cling to a token tail.
    out = out.replace(/[`'"*>]+$/, "");

    return out;
};

const clearRefreshingLink = (href: string) => {
    const updated = { ...refreshingLinkMap.value };
    delete updated[href];
    refreshingLinkMap.value = updated;
};

const fetchFreshHrefForExpiredLink = async (
    expiredHref: string,
    remotePath?: string,
): Promise<string> => {
    const cachedFresh = refreshedExpiredLinkMap.value[expiredHref];
    if (cachedFresh) {
        return cachedFresh;
    }

    const inFlight = refreshingLinkPromises.get(expiredHref);
    if (inFlight) {
        return inFlight;
    }

    const run = async () => {
        refreshingLinkMap.value = {
            ...refreshingLinkMap.value,
            [expiredHref]: true,
        };

        const resolvedRemotePath = remotePath || extractRemotePathFromAttachmentHref(expiredHref);
        if (!resolvedRemotePath) {
            throw new Error("Unable to refresh this expired attachment link.");
        }

        const tempUrl = await currentService.value.getTemporaryUrl({
            request: "get-db",
            path: resolvedRemotePath,
            expires: ATTACHMENT_URL_EXPIRES_SECONDS,
            ...getAttachmentTemporaryUrlOptions(selectedAiPlatform.value),
        });
        const freshHref = typeof tempUrl === "string" ? tempUrl : tempUrl.url;
        refreshedExpiredLinkMap.value = {
            ...refreshedExpiredLinkMap.value,
            [expiredHref]: freshHref,
        };
        ensureLinkExpiryBoundaryScheduled();
        return freshHref;
    };

    const promise = run()
        .finally(() => {
            refreshingLinkPromises.delete(expiredHref);
            clearRefreshingLink(expiredHref);
        });

    refreshingLinkPromises.set(expiredHref, promise);
    return promise;
};

// Cache blob URLs keyed by (filename + content) so that re-renders (and the
// per-tick re-renders during typewriter) don't create a fresh Blob/ObjectURL
// every time. Without this, long streaming messages leak hundreds of URLs.
const fileBlobCache = new Map<string, string>();

const getOrCreateFileHref = (filename: string, body: string) => {
    const key = `${filename}\u0000${body}`;
    const existing = fileBlobCache.get(key);
    if (existing) return existing;
    const contentType = mime.getType(filename) || 'text/plain';
    const blob = new Blob([body], { type: contentType });
    const href = URL.createObjectURL(blob);
    fileBlobCache.set(key, href);
    return href;
};

const revokeFileBlobCache = () => {
    for (const href of fileBlobCache.values()) {
        try {
            URL.revokeObjectURL(href);
        } catch {
            // ignore
        }
    }
    fileBlobCache.clear();
};

// HTML escape used when assembling anchor strings that get spliced into
// marked-rendered HTML. Both element text and attribute values use this.
const escapeAttr = (s: string): string => s.replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch] as string));

type LinkMsgPart = Extract<MsgPart, { type: 'link' }>;

// Build the anchor HTML for an extracted file fence. Embedded into the
// markdown source via placeholder; marked passes raw HTML through.
const fileToAnchorHtml = (filename: string, href: string): string => {
    const text = `\u2197 ${filename}`;
    return `<a class="bq-file-download" href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${escapeAttr(text)}</a>`;
};

// Build the anchor HTML for an extracted inline link/source. Carries
// data-* attributes that the click delegation handler reads to dispatch
// expired-link refresh through onMessageLinkClick.
const linkToAnchorHtml = (link: LinkMsgPart): string => {
    const refreshing = !!refreshingLinkMap.value[link.expiredHref || link.href];
    const cls = ['bq-link-button'];
    if (link.expired) cls.push('is-expired');
    if (refreshing) cls.push('is-refreshing');
    const labelText = `\u2197 ${link.label}${refreshing ? ' (fetching...)' : ''}`;
    const attrs = [
        `class="${cls.join(' ')}"`,
        `href="${escapeAttr(link.href)}"`,
        `target="_blank"`,
        `rel="noopener noreferrer"`,
        `title="${escapeAttr(link.fullLabel || link.label)}"`,
        `download="${escapeAttr(link.fullLabel || link.label)}"`,
        `data-bq-link="1"`,
    ];
    if (link.expired) attrs.push(`data-bq-expired="1"`);
    if (link.expiredHref) attrs.push(`data-bq-expired-href="${escapeAttr(link.expiredHref)}"`);
    if (link.remotePath) attrs.push(`data-bq-remote-path="${escapeAttr(link.remotePath)}"`);
    if (link.fullLabel) attrs.push(`data-bq-full-label="${escapeAttr(link.fullLabel)}"`);
    return `<a ${attrs.join(' ')}>${escapeAttr(labelText)}</a>`;
};

// Resolve a regex match from createInlineLinkRegex into a LinkMsgPart, or
// null if the match doesn't translate (e.g. unparseable storage path). For
// `src::<token>` matches the `tail` is any trailing text the token had
// trimmed off (punctuation/wrappers) — emitted as plain text after the
// link so the surrounding sentence reads naturally.
const buildLinkPartFromGroups = (
    full: string,
    g1: string | undefined,
    g2: string | undefined,
    g3: string | undefined,
    g4: string | undefined,
    g5: string | undefined,
    g6: string | undefined,
): { part: LinkMsgPart; tail?: string } | null => {
    const dbHostPrefix = `https://db.${hostDomain}`;
    if (g1) {
        const rawPath = normalizeTrailingInlineToken(g1);
        const consumed = `src::${rawPath}`;
        const tail = full.slice(consumed.length);
        const isUrl = /^https?:\/\//i.test(rawPath);
        if (isUrl && /^https:\/\//i.test(rawPath) && !rawPath.toLowerCase().startsWith(dbHostPrefix.toLowerCase())) {
            return {
                part: {
                    type: 'link',
                    label: truncateLabelForDisplay(rawPath),
                    fullLabel: rawPath,
                    href: rawPath,
                    expired: false,
                },
                tail,
            };
        }
        const remotePath = isUrl
            ? (extractRemotePathFromAttachmentHref(rawPath) || normalizeAttachmentPathCandidate(rawPath))
            : normalizeAttachmentPathCandidate(rawPath);
        if (!remotePath) return null;
        const expiredHref = buildDisplayExpiredAttachmentHref(remotePath, remotePath);
        const cachedFreshHref = refreshedExpiredLinkMap.value[expiredHref];
        return {
            part: {
                type: 'link',
                label: truncateLabelForDisplay(remotePath),
                fullLabel: remotePath,
                href: cachedFreshHref || expiredHref,
                expired: !cachedFreshHref,
                expiredHref,
                remotePath,
            },
            tail,
        };
    }
    if (g4 && g5) {
        const rawPath = g5;
        const remotePath = normalizeAttachmentPathCandidate(rawPath);
        if (!remotePath) return null;
        const expiredHref = buildDisplayExpiredAttachmentHref(remotePath, remotePath);
        const cachedFreshHref = refreshedExpiredLinkMap.value[expiredHref];
        return {
            part: {
                type: 'link',
                label: truncateLabelForDisplay(g4),
                fullLabel: g4,
                href: cachedFreshHref || expiredHref,
                expired: !cachedFreshHref,
                expiredHref,
                remotePath,
            },
        };
    }
    const originalHref = g3 || g6 || '';
    if (!originalHref) return null;
    if (/^https:\/\//i.test(originalHref) && !originalHref.toLowerCase().startsWith(dbHostPrefix.toLowerCase())) {
        const plainLabel = g2 || originalHref;
        return {
            part: {
                type: 'link',
                label: truncateLabelForDisplay(plainLabel),
                fullLabel: plainLabel,
                href: originalHref,
                expired: false,
            },
        };
    }
    const remotePath = extractRemotePathFromAttachmentHref(originalHref);
    const fallbackLabel = g2 || originalHref;
    const expiredHref = remotePath
        ? buildDisplayExpiredAttachmentHref(remotePath, fallbackLabel)
        : originalHref;
    const cachedFreshHref = refreshedExpiredLinkMap.value[expiredHref];
    const expired = !!remotePath && !cachedFreshHref;
    const fullLabel = remotePath
        ? getExpiredAttachmentVisiblePath(remotePath, g2 || originalHref)
        : (g2 || originalHref);
    return {
        part: {
            type: 'link',
            label: truncateLabelForDisplay(fullLabel),
            fullLabel,
            href: cachedFreshHref || expiredHref,
            expired,
            expiredHref,
            remotePath: remotePath || undefined,
        },
    };
};

const parseMsgParts = (content: string): MsgPart[] => {
    // Replace files/links/sources in the content with unique placeholder
    // tokens, run the result through `marked` for full markdown support
    // (bold, italic, headings, lists, tables, blockquotes, code blocks,
    // etc.), then splice the placeholders back as anchor HTML. The whole
    // message bubble is then rendered with a single v-html, and click
    // delegation on the bubble dispatches expired-link refreshes through
    // the existing onMessageLinkClick logic.
    const placeholderHtml: string[] = [];
    // Use private-use Unicode codepoints as placeholder fences so they
    // never collide with normal text and survive marked unchanged.
    const PH = (idx: number) => `\uE000BQ${idx}\uE001`;
    const pushPlaceholder = (anchorHtml: string) => {
        const idx = placeholderHtml.length;
        placeholderHtml.push(anchorHtml);
        return PH(idx);
    };

    // 1. Replace closed file fences ```filename.ext\n...\n``` with
    //    placeholders pointing at a `<a class="bq-file-download">` anchor.
    let working = content.replace(
        /```([\w.-]+\.[a-zA-Z0-9]+)\n([\s\S]*?)```/g,
        (_full, filename: string, body: string) =>
            pushPlaceholder(fileToAnchorHtml(filename, getOrCreateFileHref(filename, body))),
    );

    // 2. Mid-typewriter unclosed fence: hide the streaming source until
    //    the closing ``` arrives so the user doesn't watch literal code
    //    scroll past character by character.
    if (typing.value) {
        const openFence = working.match(/```([\w.-]+\.[a-zA-Z0-9]+)\n?/);
        if (openFence && typeof openFence.index === 'number') {
            const dots = '.'.repeat((dotTick.value % 3) + 1);
            working = working.slice(0, openFence.index) + `\n[generating ${openFence[1]}${dots}]`;
        }
    }

    // 3. Mask inline backtick code spans so that markdown links / src::
    //    tokens *inside* code spans are not extracted (matches existing
    //    behavior — anything inside backticks is shown literally).
    const codeMasks: string[] = [];
    working = working.replace(/`[^`\n]+`/g, (match) => {
        const idx = codeMasks.length;
        codeMasks.push(match);
        return `\uE002C${idx}\uE003`;
    });

    // 4. Replace inline link tokens with placeholders. Reuse the existing
    //    branch logic via buildLinkPartFromGroups so behavior is identical
    //    to the pre-markdown renderer.
    const linkRe = createInlineLinkRegex();
    working = working.replace(linkRe, (full, ...args) => {
        const [g1, g2, g3, g4, g5, g6] = args as (string | undefined)[];
        const built = buildLinkPartFromGroups(full, g1, g2, g3, g4, g5, g6);
        if (!built) return full;
        const placeholder = pushPlaceholder(linkToAnchorHtml(built.part));
        return placeholder + (built.tail || '');
    });

    // 5. Restore the masked inline-code spans so marked formats them.
    working = working.replace(/\uE002C(\d+)\uE003/g, (_m, idx) => codeMasks[Number(idx)] || '');

    // 6. Render markdown. GFM tables, fenced code, etc. `breaks: true`
    //    keeps single newlines as <br>, matching plain-text chat feel.
    const html = marked.parse(working, {
        gfm: true,
        breaks: true,
        async: false,
    }) as string;

    // 7. Splice placeholders back in.
    const finalHtml = html.replace(
        /\uE000BQ(\d+)\uE001/g,
        (_m, idx) => placeholderHtml[Number(idx)] || '',
    );

    return [{ type: 'html', html: finalHtml }];
};

const onMessageLinkClick = async (e: MouseEvent, part: MsgPart, anchorOverride?: HTMLAnchorElement | null) => {
    if (part.type !== "link" || !part.expired) {
        return;
    }

    const originalHref = part.expiredHref || part.href;
    const anchor = anchorOverride
        ?? (e.currentTarget instanceof HTMLAnchorElement ? e.currentTarget : null);
    const cachedFresh = refreshedExpiredLinkMap.value[originalHref];

    if (cachedFresh) {
        part.expired = false;
        part.href = cachedFresh;
        if (anchor) {
            anchor.href = cachedFresh;
        }
        return;
    }

    e.preventDefault();

    try {
        const freshHref = await fetchFreshHrefForExpiredLink(originalHref, part.remotePath);

        part.expired = false;
        part.href = freshHref;
        if (anchor) {
            anchor.href = freshHref;
            anchor.click();
            return;
        }

        const replayAnchor = document.createElement("a");
        replayAnchor.href = freshHref;
        replayAnchor.target = "_blank";
        replayAnchor.rel = "noopener noreferrer";
        replayAnchor.style.display = "none";
        document.body.appendChild(replayAnchor);
        replayAnchor.click();
        replayAnchor.remove();
    } catch (err) {
        console.error("Failed to refresh expired attachment URL", err);
        alert("Failed to refresh this expired link. Please try again.");
    }
};

// Click delegation for the rendered-markdown bubble. Inline link/source
// anchors live inside the v-html string, so the bubble's `@click` is the
// only handler that fires. We reconstruct a synthetic MsgPart from the
// anchor's data attributes and forward to onMessageLinkClick. Plain links
// (no `data-bq-expired`) just navigate normally.
const onBubbleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const anchor = target.closest('a[data-bq-link]') as HTMLAnchorElement | null;
    if (!anchor) return;
    if (anchor.dataset.bqExpired !== '1') return;
    const part: MsgPart = {
        type: 'link',
        label: (anchor.textContent || '').replace(/^\u2197\s?/, '').replace(/\s*\(fetching\.{3}\)\s*$/, ''),
        fullLabel: anchor.dataset.bqFullLabel,
        href: anchor.href,
        expired: true,
        expiredHref: anchor.dataset.bqExpiredHref,
        remotePath: anchor.dataset.bqRemotePath,
    };
    onMessageLinkClick(e, part, anchor);
};

// Core: dispatch an AI request and commit its result to the shared history cache.
// This runs independently of the component lifecycle so a "Thinking..." request
// keeps running if the user navigates away and back. The returned promise resolves
// once the assistant reply (or error) has been appended to aiChatHistoryCache.
const dispatchAgentRequest = (params: {
    key: string;
    serviceId: string;
    owner: string;
    aiPlatform: "claude" | "openai";
    aiModel?: string;
    systemPrompt: string;
    text: string;
    boundedMessages: ClaudeMessage[];
}): Promise<void> => {
    const appendToCache = (msg: ChatMessage) => {
        const existing = aiChatHistoryCache[params.key] || {
            messages: [],
            endOfList: false,
            startKeyHistory: [],
        };
        aiChatHistoryCache[params.key] = {
            ...existing,
            messages: [...existing.messages, msg],
        };
    };

    const run = async () => {
        try {
            const callProvider = () => params.aiPlatform === "openai"
                ? callOpenAIWithPublicMcp(
                      params.text,
                      params.serviceId,
                      params.owner,
                      params.boundedMessages,
                      params.systemPrompt,
                      params.aiModel,
                  )
                : callClaudeWithPublicMcp(
                      params.text,
                      params.serviceId,
                      params.owner,
                      params.boundedMessages,
                      params.systemPrompt,
                      params.aiModel,
                  );

            const refreshAuthAndRetry = async () => {
                // `$ACCESS_TOKEN` is resolved by clientSecretRequest from the
                // live Skapi session headers. Refresh that session before retry.
                await refreshSkapiSession();
                return callProvider();
            };

            let response: any;
            try {
                response = await callProvider();
            } catch (err: any) {
                if (isAuthExpiredError(err)) {
                    response = await refreshAuthAndRetry();
                } else {
                    throw err;
                }
            }
            if (isErrorResponseBody(response) && isAuthExpiredError(response)) {
                try {
                    response = await refreshAuthAndRetry();
                } catch (_retryErr) { /* fall through to normal error rendering */ }
            }
            // console.log("Agent request resolved with response:", response);
            if (isErrorResponseBody(response)) {
                appendToCache({
                    role: "assistant",
                    content: getErrorMessage(response),
                    isError: true,
                });
                return;
            }

            const answer = (params.aiPlatform === "openai"
                ? extractOpenAIText(response)
                : extractClaudeText(response))?.trim();

            appendToCache({
                role: "assistant",
                content: answer || "No text response received from AI provider.",
                isError: false,
            });
        } catch (err: any) {
            const errorMessage = err instanceof SkapiError
                ? err.message
                : getErrorMessage(err);
            appendToCache({
                role: "assistant",
                content: errorMessage,
                isError: true,
            });
        } finally {
            delete pendingAgentRequests[params.key];
        }
    };

    const promise = run();
    pendingAgentRequests[params.key] = promise;
    return promise;
};

// When getChatHistory returns a `.pending` array of promises, each promise
// represents a chat request in flight on the server (started in another tab,
// before a refresh, etc.) and will resolve to a response body identical to a
// live chat response. For each pending promise we append an assistant
// placeholder with `isPending: true` so the Thinking indicator shows, then
// replace the placeholder in place when its promise resolves.
const attachPendingHistoryRequests = (
    key: string,
    pendingList: Promise<any>[] | undefined,
    platform: "claude" | "openai",
) => {
    if (!Array.isArray(pendingList) || !pendingList.length) return;

    const resolveToMessage = (response: any): ChatMessage => {
        if (isErrorResponseBody(response)) {
            return {
                role: "assistant",
                content: getErrorMessage(response),
                isError: true,
            };
        }
        const answer = (platform === "openai"
            ? extractOpenAIText(response)
            : extractClaudeText(response))?.trim();
        return {
            role: "assistant",
            content: answer || "No text response received from AI provider.",
        };
    };

    // Append placeholder assistant messages (one per pending promise) and
    // remember their indices so we can replace them in place as each resolves.
    const placeholderIndices: number[] = [];
    for (let i = 0; i < pendingList.length; i++) {
        messages.value.push({ role: "assistant", content: "", isPending: true });
        placeholderIndices.push(messages.value.length - 1);
    }
    updateHistoryCache();

    const runAll = async () => {
        try {
            await Promise.all(
                pendingList.map(async (pending, i) => {
                    let resolved: ChatMessage;
                    try {
                        const response = await pending;
                        resolved = resolveToMessage(response);
                    } catch (err: any) {
                        resolved = {
                            role: "assistant",
                            content: err instanceof SkapiError
                                ? err.message
                                : getErrorMessage(err),
                            isError: true,
                        };
                    }

                    const idx = placeholderIndices[i];
                    const target = messages.value[idx];
                    if (target && target.isPending) {
                        if (resolved.isError || !resolved.content) {
                            // Errors / empty replies: swap in immediately.
                            messages.value[idx] = resolved;
                        } else {
                            // Reveal text via the shared typewriter queue so
                            // the user can read it (and can't fire another
                            // request mid-reveal). Stage the message with
                            // empty content first so Thinking goes away.
                            messages.value[idx] = {
                                ...resolved,
                                content: "",
                            };
                            void enqueueTypewrite(idx, resolved.content);
                        }
                    }

                    // Mirror into shared cache so navigation away and back
                    // preserves the resolved text.
                    const cached = aiChatHistoryCache[key];
                    if (cached) {
                        const cachedTarget = cached.messages[idx];
                        if (cachedTarget && cachedTarget.isPending) {
                            cached.messages[idx] = resolved;
                        }
                    }
                }),
            );
        } finally {
            delete pendingAgentRequests[key];
        }
    };

    pendingAgentRequests[key] = runAll();
};

// Reveal the latest assistant reply character-by-character so the user has
// time to read it and cannot immediately fire another request. Runs after the
// full response is already in aiChatHistoryCache; local `messages.value` is
// updated progressively. Errors are shown immediately (no typing effect).
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Reveal `fullText` into messages.value[idx].content character-by-character.
// Shared by both the live-reply typewriter and the pending-history typewriter.
const typewriteIntoIndex = async (idx: number, fullText: string) => {
    if (!fullText) return;

    const TICK_MS = 16;
    const charsPerTick = 3; // ~180 CPM per tick, or ~11K CPM for a full-speed 60fps stream. Adjust as desired.

    // Fast-forward through fenced file blocks: while the typewriter is inside
    // a ```filename.ext\n...\n``` region it's rendered as a [generating ...]
    // placeholder anyway, so there's no reason to stream those chars slowly.
    const FENCE_REVEAL_MS = 200;
    const fenceTicks = Math.max(1, Math.floor(FENCE_REVEAL_MS / TICK_MS));
    const fenceRegions: { start: number; end: number }[] = [];
    const fenceRegex = /```[\w.-]+\.[a-zA-Z0-9]+\n[\s\S]*?```/g;
    let fm: RegExpExecArray | null;
    while ((fm = fenceRegex.exec(fullText)) !== null) {
        fenceRegions.push({ start: fm.index, end: fm.index + fm[0].length });
    }

    const linkRegions: { start: number; end: number }[] = [];
    const linkRegex = createInlineLinkRegex();
    let lm: RegExpExecArray | null;
    while ((lm = linkRegex.exec(fullText)) !== null) {
        linkRegions.push({ start: lm.index, end: lm.index + lm[0].length });
    }

    typing.value = true;
    typingAbort = false;

    try {
        let i = 0;
        while (i < fullText.length) {
            if (typingAbort) break;
            let step = charsPerTick;
            const region = fenceRegions.find((r) => i >= r.start && i < r.end);
            const linkRegion = linkRegions.find((r) => i >= r.start && i < r.end);
            if (region) {
                step = Math.max(
                    charsPerTick,
                    Math.ceil((region.end - i) / fenceTicks),
                );
            } else if (linkRegion) {
                step = Math.max(charsPerTick, linkRegion.end - i);
            } else {
                const nextLinkRegion = linkRegions.find((r) => i < r.start && i + step > r.start);
                if (nextLinkRegion) {
                    step = nextLinkRegion.end - i;
                }
            }
            i = Math.min(fullText.length, i + step);
            const target = messages.value[idx];
            if (!target) break;
            target.content = fullText.slice(0, i);
            await scrollToBottom();
            await sleep(TICK_MS);
        }
    } finally {
        // Always settle to the full text so partial reveals don't stick on abort.
        const target = messages.value[idx];
        if (target) target.content = fullText;
        typing.value = false;
    }
};

// Sequential queue so multiple typewriters (e.g. several pending-history
// resolutions) play one after another instead of fighting over `typing` /
// `typingAbort`.
let typewriterQueue: Promise<void> = Promise.resolve();
const enqueueTypewrite = (idx: number, fullText: string) => {
    typewriterQueue = typewriterQueue.then(() => typewriteIntoIndex(idx, fullText));
    return typewriterQueue;
};

const typewriteLatestReply = async (key: string) => {
    const cached = aiChatHistoryCache[key];
    if (!cached || !cached.messages.length) return;

    const latest = cached.messages[cached.messages.length - 1];
    if (latest.role !== "assistant" || latest.isError || !latest.content) {
        messages.value = [...cached.messages];
        return;
    }

    // Seed the local array with everything except the final assistant message,
    // then append a placeholder we can mutate in place for the reveal effect.
    messages.value = [
        ...cached.messages.slice(0, -1),
        { role: "assistant", content: "", isError: false },
    ];

    await typewriteIntoIndex(messages.value.length - 1, latest.content);
};

// If a request is already in flight for this cache key, show the thinking
// indicator and wait for it. When it resolves, refresh messages from the
// shared cache so the assistant reply (pushed by dispatchAgentRequest while
// the component may have been unmounted) appears in the UI.
const resumePendingRequest = async (token: number) => {
    const key = getHistoryCacheKey();
    const pending = key ? pendingAgentRequests[key] : undefined;
    if (!pending) return;

    // If the pending work is from getChatHistory (each pending item renders
    // its own Thinking placeholder), skip the sending/typewriter flow - those
    // placeholders replace themselves in place as the promises resolve.
    if (messages.value.some((m) => m.isPending)) return;

    sending.value = true;
    await scrollToBottom(true);

    try {
        await pending;
    } catch {
        // dispatchAgentRequest already captures errors into the cache
    }

    if (token !== gateRefreshToken) return;

    sending.value = false;
    await typewriteLatestReply(key);
    await scrollToBottom(true);
};

// Build the system prompt fresh on every send. The project name/description
// can change at any time (settings page edits, another tab, etc.) so we
// can't precompute or memoize this string.
const buildSystemPrompt = (): string => {
    const svc = currentService.value?.service;
    const formattedServiceId = svc?.formattedServiceId ?? "";

    let systemPrompt = `
You are a dedicated assistant for the project ID: "${formattedServiceId}".
Scope: Only answer questions about this project and its data. Do not answer questions about other projects or topics unrelated to this project. When the user refers to "my database", "my data", or "my files", treat those as references to this project's database and file storage.
Knowledge lookup: Before saying you don't know or that something isn't in the chat history, ALWAYS query this project's database through the available MCP tools to look for the answer. The user's data is the source of truth - the chat transcript is not. Only respond with "I don't know" or "I couldn't find that" after you have actually searched the project's data and come back empty.
File attachments: When a user message contains an "Attached files:" section with markdown links, those links point to short-lived signed URLs in this project's db storage and will expire.
- Image files (.jpg, .jpeg, .png, .gif, .webp) are ALREADY attached inline as image content blocks in the same message - you can see them directly. Do NOT call web_fetch on image URLs; that will fail or return garbage. Just look at the image block and answer.
- For all other file types (text, code, csv, json, pdf, etc.), use your web_fetch tool to download and read each URL before answering. Treat the fetched contents as user-supplied input data. Do not ask the user to paste the file contents - fetch the URLs yourself.
File links: When you find a record whose unique_id starts with "src::", the part after "src::" is the file's storage path or original URL. Always present it as a markdown link so the user can access it. Strip the "src::" prefix — do NOT show it. Format: [filename](path/to/file) for storage paths, or [filename](https://...) for external URLs. Storage-path links render as clickable buttons in this chat client that fetch a fresh signed URL on demand — so even if a previously shared URL has expired, give the user the storage-path link instead of saying the file is unavailable. Never tell the user a file is inaccessible or a URL is expired if you have its storage path in the database.
File lookup: When the user asks to see, list, or show files (e.g. "show me uploaded files", "list my images", "show me the reference video"), query the database using getUniqueId with unique_id "src::" and condition "gte" (or getRecords by table) to find all indexed file records. Present each result as a markdown link as described above. Never say you cannot access file storage — the file paths are indexed in the database and are always reachable through it.
File generation: If the user asks you to generate a file and it is possible to do so, output the file contents inside a fenced code block using the file extension as the language identifier. Always use plain text - never base64 or other encodings. Example for CSV:
\`\`\`filename.csv
item,qty,total
Carrots,55,$38.50
Mushrooms,41,$73.80
Zucchini,29,$43.50
\`\`\`
The same pattern applies to other formats: \`\`\`my-data.json, \`\`\`index.html, \`\`\`sample.txt, etc.`;

    if (svc?.description) {
        systemPrompt += `
Project name: "${svc.name ?? ""}"
Project description: """${svc.description}"""`;
    }

    return systemPrompt;
};

const sendMessage = async () => {
    const text = prompt.value.trim();
    const hasAttachments = attachments.value.length > 0;
    if ((!text && !hasAttachments) || !chatEnabled.value || sending.value || uploadingAttachments.value) {
        return;
    }

    const aiPlatform = selectedAiPlatform.value;
    if (aiPlatform === "none") {
        messages.value.push({ role: "user", content: text || "(attachments only)" });
        messages.value.push({
            role: "assistant",
            content: "AI platform is not configured.",
            isError: true,
        });
        updateHistoryCache();
        prompt.value = "";
        clearAttachments();
        await nextTick();
        autoGrowInput();
        await scrollToBottom(true);
        return;
    }

    const key = getHistoryCacheKey();
    if (pendingAgentRequests[key]) {
        // a request is already in flight for this conversation; ignore
        return;
    }

    // Upload attachments (if any) BEFORE adding the user message to history,
    // so we can append the resulting links into the same message.
    let attachmentUrls: { name: string; url: string }[] = [];
    if (hasAttachments) {
        try {
            attachmentUrls = await uploadPendingAttachments();
        } catch (err: any) {
            const msg =
                err && err.message ? String(err.message) : "Failed to upload attachments";
            messages.value.push({
                role: "assistant",
                content: `Attachment upload failed: ${msg}`,
                isError: true,
            });
            await nextTick();
            await scrollToBottom(true);
            return;
        }
    }

    // Build the message body: user text + a markdown list of attached files.
    // The URLs are short-lived signed URLs to the project's db storage. The
    // system prompt already instructs the model to fetch any "Attached files:"
    // links via web_fetch, so we don't repeat that instruction per-message
    // (it would just bloat history and clutter the visible chat).
    let composed = text;
    if (attachmentUrls.length > 0) {
        const lines = attachmentUrls.map(
            (u) => `- [${u.name}](${u.url})`
        );
        const block = `Attached files:\n${lines.join("\n")}`;
        composed = text ? `${text}\n\n${block}` : block;
    }

    messages.value.push({ role: "user", content: composed });
    updateHistoryCache();
    prompt.value = "";
    clearAttachments();
    await nextTick();
    autoGrowInput();
    sending.value = true;
    await scrollToBottom(true);

    const aiModel = selectedAiModel.value || undefined;
    const systemPrompt = buildSystemPrompt();
    const boundedPayload = buildBoundedChatMessages({
        platform: aiPlatform,
        model: aiModel,
        systemPrompt,
        history: messages.value,
    });

    debugAgentLoad("chat payload", {
        platform: aiPlatform,
        model: aiModel,
        messageCount: boundedPayload.messages.length,
        droppedCount: boundedPayload.droppedCount,
        estimatedInputTokens: boundedPayload.estimatedInputTokens,
        estimatedBudget: boundedPayload.estimatedBudget,
    });

    const serviceId = currentService.value.id;
    const owner = currentService.value.owner;
    const requestToken = gateRefreshToken;

    const promise = dispatchAgentRequest({
        key,
        serviceId,
        owner,
        aiPlatform,
        aiModel,
        systemPrompt,
        text: composed,
        boundedMessages: boundedPayload.messages,
    });

    try {
        await promise;
    } finally {
        if (requestToken === gateRefreshToken && getHistoryCacheKey() === key) {
            sending.value = false;
            await typewriteLatestReply(key);
            await scrollToBottom(true);
        }
    }
};

watch(
    () => [currentService.value?.id, currentService.value?.service?.ai_agent],
    () => {
        void refreshGate();
    }
);

// External surfaces (e.g. dbfile.vue after a batch upload completes) bump
// `agentHistoryRefreshTrigger[serviceId]` to ask us to refetch chat history
// so background-indexing requests show up as pending bubbles. The proxy
// enqueues the request asynchronously \u2014 the queue entry may not be
// observable in clientSecretRequestHistory for a few seconds after the
// upload finishes \u2014 so we don't fire a single-shot refetch. Instead we
// snapshot the set of currently-known pending ids, then poll the first page
// at INTERVAL_MS until we see a NEW pending id (or hit MAX_ATTEMPTS, to
// avoid runaway polling on misconfigured services). Once a new pending id
// is observed, the existing `schedulePendingHistoryRefetch` loop fired by
// fetchHistoryPage takes over and keeps polling until the request resolves.
//
// Watched as a *deep* watch on the whole trigger object so newly-added
// service-id keys are picked up even if the slot didn't exist when the
// watcher was first installed.
const QUEUE_DETECT_INITIAL_DELAY_MS = 500;
const QUEUE_DETECT_INTERVAL_MS = 2500;
const QUEUE_DETECT_MAX_ATTEMPTS = 8; // ~20s of polling after the initial delay
let agentHistoryRefreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let queueDetectAttempts = 0;
let queueDetectBaselineIds: Set<string> = new Set();

const cancelQueueDetectPoll = () => {
    if (agentHistoryRefreshDebounceTimer) {
        clearTimeout(agentHistoryRefreshDebounceTimer);
        agentHistoryRefreshDebounceTimer = null;
    }
    queueDetectAttempts = 0;
};

const startQueueDetectPoll = () => {
    cancelQueueDetectPoll();
    if (agentUnmounted) return;
    if (!chatEnabled.value || selectedAiPlatform.value === "none") return;

    // Snapshot the pending server-item ids we already know about. The poll
    // loop completes the moment the refreshed history contains a pending
    // id NOT in this set, indicating the new queue entry has been
    // registered server-side.
    queueDetectBaselineIds = new Set(
        messages.value
            .filter((m) => m.isPending && typeof m._serverItemId === "string")
            .map((m) => m._serverItemId as string)
    );
    queueDetectAttempts = 0;

    const tick = async () => {
        agentHistoryRefreshDebounceTimer = null;
        if (agentUnmounted) return;
        if (!chatEnabled.value || selectedAiPlatform.value === "none") return;
        queueDetectAttempts++;

        await fetchHistoryPage(false, gateRefreshToken, true);

        if (agentUnmounted) return;

        // Did a brand-new pending id appear? If so, the upload's queue
        // entry is now observable; let schedulePendingHistoryRefetch take
        // over and stop our detect loop.
        const sawNewPending = messages.value.some(
            (m) =>
                m.isPending &&
                typeof m._serverItemId === "string" &&
                !queueDetectBaselineIds.has(m._serverItemId as string)
        );
        if (sawNewPending) {
            queueDetectAttempts = 0;
            return;
        }

        if (queueDetectAttempts >= QUEUE_DETECT_MAX_ATTEMPTS) {
            queueDetectAttempts = 0;
            return;
        }

        agentHistoryRefreshDebounceTimer = setTimeout(() => {
            void tick();
        }, QUEUE_DETECT_INTERVAL_MS);
    };

    agentHistoryRefreshDebounceTimer = setTimeout(() => {
        void tick();
    }, QUEUE_DETECT_INITIAL_DELAY_MS);
};

watch(
    agentHistoryRefreshTrigger,
    () => {
        if (agentUnmounted) return;
        const serviceId = currentService.value?.id;
        if (!serviceId) return;
        const cur = agentHistoryRefreshTrigger[serviceId];
        if (typeof cur !== "number") return;
        const last = agentHistoryRefreshLastSeen[serviceId];
        if (last === cur) return;
        agentHistoryRefreshLastSeen[serviceId] = cur;
        if (!chatEnabled.value || selectedAiPlatform.value === "none") return;
        startQueueDetectPoll();
    },
    { deep: true }
);

watch(
    () => agentRoot.value,
    (root) => {
        if (root) {
            scheduleAgentViewportHeight();
        }
    },
    { flush: "post" }
);

// Animate the dots in [generating file.ext...] placeholders while any
// typewriter is running. Stop the interval when typing ends to avoid
// pointless re-renders.
watch(typing, (isTyping) => {
    if (isTyping) {
        if (dotTickTimer) return;
        dotTick.value = 0;
        dotTickTimer = setInterval(() => {
            dotTick.value = (dotTick.value + 1) % 3;
        }, 400);
    } else if (dotTickTimer) {
        clearInterval(dotTickTimer);
        dotTickTimer = null;
    }
});

watch(showHistoryLoadingIndicator, (isVisible) => {
    if (isVisible) {
        if (historyLoadDotTickTimer) return;
        historyLoadDotTick.value = 0;
        historyLoadDotTickTimer = setInterval(() => {
            historyLoadDotTick.value = (historyLoadDotTick.value + 1) % 3;
        }, 400);
    } else if (historyLoadDotTickTimer) {
        clearInterval(historyLoadDotTickTimer);
        historyLoadDotTickTimer = null;
    }
});

onMounted(async () => {
    void refreshGate();
    removeFullScreenLoader();
    syncMobile();
    await nextTick();
    autoGrowInput();
    if (agentRoot.value) {
        scheduleAgentViewportHeight();
    }
    await nextTick();
    await scrollToBottom(false);
    window.addEventListener("resize", onWindowResize);
    window.addEventListener("resize", syncMobile);
    window.addEventListener("click", closeModelMenu);
    window.addEventListener("click", closePlatformMenu);

    // Re-evaluate attachment chip truncation whenever the chat container
    // resizes (e.g. agent viewport height settles, sidebar toggles).
    if (chatBox.value && typeof ResizeObserver !== "undefined") {
        attachmentsResizeObserver = new ResizeObserver(() => {
            scheduleAttachmentRecompute();
        });
        attachmentsResizeObserver.observe(chatBox.value);
    }
});

onUnmounted(() => {
    agentUnmounted = true;
    typingAbort = true;
    if (dotTickTimer) {
        clearInterval(dotTickTimer);
        dotTickTimer = null;
    }
    if (historyLoadDotTickTimer) {
        clearInterval(historyLoadDotTickTimer);
        historyLoadDotTickTimer = null;
    }
    cancelPendingHistoryPoll();
    cancelQueueDetectPoll();
    revokeFileBlobCache();
    if (refreshedLinkExpiryTimer) {
        clearTimeout(refreshedLinkExpiryTimer);
        refreshedLinkExpiryTimer = null;
    }
    window.cancelAnimationFrame(viewportHeightFrame);
    window.cancelAnimationFrame(attachmentsRecomputeFrame);
    window.removeEventListener("resize", onWindowResize);
    window.removeEventListener("resize", syncMobile);
    window.removeEventListener("click", closeModelMenu);
    window.removeEventListener("click", closePlatformMenu);
    if (attachmentsResizeObserver) {
        attachmentsResizeObserver.disconnect();
        attachmentsResizeObserver = null;
    }
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-agent {
    height: var(--agent-available-height, 50dvh);
    overflow: hidden;
    font-family: @mono;
    color: @ink;
}

.bq-meta {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 1px solid @line;
    padding: 1rem;
    box-shadow: 2px 2px rgba(0, 0, 0, 0.12);
}

.bq-section-title {
    font-size: 1.1rem;
    font-weight: 700;
    // margin: 0 0 1.25rem;
    padding-bottom: 0.75rem;
    // border-bottom: 1px solid @line;
}

.bq-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;

    h2 {
        margin: 0;
        font-size: inherit;
        font-weight: inherit;
    }
}

.bq-title-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bq-title-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bq-icon {
    font-size: 1.4rem;
    color: @muted;
    transition: color 0.15s;
    user-select: none;
    vertical-align: middle;
    cursor: pointer;

    &:hover,
    &.active {
        color: @pink;
    }

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
        cursor: default;
    }

    &.bq-icon--danger {
        &:hover {
            color: @danger;
        }
    }
}

.bq-platform-logo {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
}

.bq-model-name {
    font-size: 0.75rem;
    font-weight: 400;
    color: @muted;
}

.bq-ai-select {
    position: relative;
    width: 220px;

    &.bq-ai-select--inline {
        width: auto;
        min-width: 160px;
        max-width: 240px;
    }

    &.bq-ai-select--platform {
        width: auto;
    }
}

.bq-ai-select-native {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-height: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
}

.bq-ai-select-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.78rem;
    cursor: pointer;

    &:not(:disabled):hover,
    &.open {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.bq-ai-select-value {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.bq-ai-caret {
    color: @muted;
    flex: 0 0 auto;
}

.bq-ai-options {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
}

.bq-model-options {
    max-height: 240px;
    overflow-y: auto;
}

.bq-platform-options {
    min-width: 160px;
}

.bq-ai-logo {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
}

.bq-ai-option {
    border: 0;
    border-bottom: 1px solid @line;
    background: transparent;
    box-shadow: none;
    width: 100%;
    text-align: left;
    padding: 0.4rem 0.6rem;
    font-family: @mono;
    font-size: 0.78rem;
    color: @ink;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.45rem;
    cursor: pointer;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background: @hover-bg;
    }

    &.active {
        background: @hover-bg;
    }
}

.bq-gate {
    margin-bottom: 1rem;
    padding: 0.85rem;
    border: 1px solid @line;
    background: @hover-bg;
}

.bq-gate-loading {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 0.83rem;
}

.bq-gate-title {
    margin: 0 0 0.35rem;
    font-weight: 600;
}

.bq-gate-desc {
    margin: 0 0 0.65rem;
    color: @muted;
}

.bq-link {
    color: @pink;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}


.bq-chat {
    border: none;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.bq-messages {
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-height: 0;
    max-height: none;
    padding: 0;
    border-bottom: none;
    background: @paper;
}

.bq-history-loading {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    padding: 0.5rem 0 1rem;
    text-align: center;
    font-size: 0.8rem;
    color: @muted;
    background: linear-gradient(to bottom, @paper 70%, fade(@paper-lit, 0%));
}

.bq-empty-greeting {
    .bq-bubble {
        user-select: none;
    }
}

.bq-message {
    display: flex;
    margin-bottom: 0.5rem;

    &.is-user {
        justify-content: flex-end;
    }

    &.is-assistant {
        justify-content: flex-start;
    }
}

.bq-bubble {
    // max-width: min(85%, 680px);
    padding: 0.55rem 0.7rem;
    border: 1px solid @line;
    white-space: pre-wrap;
    line-height: 1.45;
    font-size: 0.83rem;
    background: @paper;
    span {
        word-break: break-word;
    }
}

.is-user .bq-bubble {
    background: @paper;
}

.is-assistant:not(.is-error) .bq-bubble {
    background: @success-bg;
    border-color: @success-border;
}

.is-error .bq-bubble {
    background: @danger-bg;
    border-color: @danger;
    color: @danger;
}

.is-pending-older .bq-bubble {
    background: @warning-bg;
    border-color: @warning-border;
    color: @warning;
}

// Rendered-markdown wrapper inside a chat bubble. Overrides the bubble's
// `white-space: pre-wrap` since marked already produces structural HTML
// (paragraphs, lists, tables, etc.). `pre`/`code` blocks reinstate
// pre-wrap for their own content.
.bq-md {
    white-space: normal;
    word-break: break-word;
    :deep(> :first-child) { margin-top: 0; }
    :deep(> :last-child) { margin-bottom: 0; }
    :deep(p) {
        margin: 0.4em 0;
    }
    :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
        margin: 0.8em 0 0.35em;
        line-height: 1.25;
        font-weight: 600;
    }
    :deep(h1) { font-size: 1.3em; }
    :deep(h2) { font-size: 1.18em; }
    :deep(h3) { font-size: 1.08em; }
    :deep(h4), :deep(h5), :deep(h6) { font-size: 1em; }
    :deep(ul), :deep(ol) {
        margin: 0.4em 0;
        padding-left: 1.4em;
    }
    :deep(li) {
        margin: 0.15em 0;
    }
    :deep(li > p) {
        margin: 0.2em 0;
    }
    :deep(blockquote) {
        margin: 0.5em 0;
        padding: 0.2em 0.8em;
        border-left: 3px solid @line;
        color: #555;
        background: rgba(0, 0, 0, 0.03);
    }
    :deep(hr) {
        border: none;
        border-top: 1px solid @line;
        margin: 0.8em 0;
    }
    :deep(a) {
        color: @pink;
        text-decoration: underline;
    }
    :deep(a:hover) {
        text-decoration: none;
    }
    :deep(code) {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 0.9em;
        padding: 0.1em 0.35em;
        background: rgba(0, 0, 0, 0.06);
        border-radius: 0;
        white-space: pre-wrap;
        word-break: break-word;
    }
    :deep(pre) {
        margin: 0.5em 0;
        padding: 0.6em 0.8em;
        background: @code-bg;
        color: @code-fg;
        border-radius: 0;
        overflow-x: auto;
        white-space: pre;
    }
    :deep(pre code) {
        background: transparent;
        color: inherit;
        padding: 0;
        font-size: 0.85em;
        white-space: pre;
        word-break: normal;
    }
    :deep(table) {
        border-collapse: collapse;
        margin: 0.5em 0;
        font-size: 0.95em;
        display: block;
        overflow-x: auto;
    }
    :deep(th), :deep(td) {
        border: 1px solid @line;
        padding: 0.3em 0.55em;
        text-align: left;
    }
    :deep(th) {
        background: rgba(0, 0, 0, 0.04);
        font-weight: 600;
    }
    :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
    }
    :deep(strong) { font-weight: 600; }
    :deep(em) { font-style: italic; }
    :deep(del) { text-decoration: line-through; }
}

.bq-pending-note {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.7rem;
    color: @warning;
    font-style: italic;
}

.bq-file-download {
    color: @pink;
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0;
    font: inherit;
    vertical-align: baseline;

    &:hover {
        color: @pink;
        text-decoration: underline;
        background: transparent;
    }
}

.bq-link-button {
    .bq-file-download();
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bq-input-row {
    display: flex;
    flex-shrink: 0;
    --chat-control-height: 2.6rem;
    gap: 0.65rem;
    padding: 0.65rem 0 0;
    background: @paper;
    flex-wrap: wrap;
}

.bq-attach-input {
    display: none;
}

.bq-input-wrap {
    position: relative;
    flex: 1;
    display: flex;
    min-width: 0;
}

.bq-attach-btn {
    position: absolute;
    top: 0;
    left: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--chat-control-height);
    height: var(--chat-control-height);
    min-height: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: @muted;
    cursor: pointer;
    z-index: 1;
    box-shadow: none;
    border-radius: 0;

    &:hover:not(:disabled) {
        background: transparent;
        color: @pink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.bq-attachments {
    flex-basis: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0 0 0.5rem;
}

.bq-attachment-warning {
    flex-basis: 100%;
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 0.4rem 0.5rem;
    margin-bottom: 0.4rem;
    border: 1px solid @danger;
    background: fade(@danger-lit, 8%);
    color: @danger;
    font-size: 0.75rem;
    line-height: 1.3;
}

.bq-attachment-warning-icon {
    font-size: 1rem;
    line-height: 1.3;
    flex: 0 0 auto;
}

.bq-attachment {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: 18rem;
    padding: 0.3rem 0.5rem;
    border: 1px solid @line;
    background: @paper;
    font-size: 0.78rem;
    font-family: @mono;
    position: relative;
    overflow: hidden;
    isolation: isolate;

    &.is-uploading {
        &::before {
            content: "";
            position: absolute;
            inset: 0 auto 0 0;
            width: var(--att-progress, 0%);
            background: fade(@pink-lit, 18%);
            transition: width 0.15s linear;
            z-index: 0;
            pointer-events: none;
        }

        > * {
            position: relative;
            z-index: 1;
        }
    }

    &.is-error {
        border-color: #c0392b;

        .bq-attachment-meta {
            color: #c0392b;
        }
    }

    &.is-clickable {
        cursor: pointer;
        transition: background 0.12s ease, border-color 0.12s ease;

        &:hover {
            background: @hover-bg;
            border-color: @ink;
        }
    }
}

.bq-attachment-icon {
    flex: 0 0 16px;
    color: @muted;
}

.bq-attachment-arrow {
    flex: 0 0 auto;
    font-size: 0.95rem;
    line-height: 1;
    color: @muted;
}

.is-done .bq-attachment-arrow {
    color: @ink;
}

.bq-attachment-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 12rem;
}

.bq-attachment-meta {
    color: @muted;
    flex-shrink: 0;
}

.bq-attachment-more {
    color: @muted;
    font-style: italic;
    cursor: default;
}

.bq-attachment-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;
    height: 1.2rem;
    margin-left: 0.15rem;
    padding: 0;
    border: none;
    background: transparent;
    color: @muted;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: @ink;
    }
}

.bq-chat {
    position: relative;
}

.bq-drop-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    background: fade(@paper-lit, 85%);
    border: 2px dashed @pink;
    pointer-events: none;
}

.bq-drop-overlay-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: @pink;
    font-size: 0.9rem;
    font-weight: 600;
}

.bq-apikey-overlay {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    background: fade(@paper-lit, 85%);
    padding: 1rem;
}

.bq-apikey-overlay-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.85rem;
    text-align: center;
    max-width: 32rem;

    .btn {
        align-self: center;
        min-width: 11rem;
    }

    // Keep the button in its normal active styling while we're fetching the
    // previous API key — disabled fades it out and on mobile the lingering
    // :hover state flips it to white, which makes the dots animation invisible.
    .btn.is-loading-key,
    .btn.is-loading-key:hover,
    .btn.is-loading-key:disabled,
    .btn.is-loading-key:focus,
    .btn.is-loading-key:active {
        opacity: 1;
        background: @ink;
        color: @paper;
        cursor: progress;
    }
}

.bq-apikey-logo {
    width: 3rem;
    height: 3rem;
    object-fit: contain;
}

.bq-apikey-help {
    margin: 0;
    color: @muted;
    font-size: 0.83rem;
    line-height: 1.4;
}

.bq-apikey-error {
    margin: 0;
    color: @danger;
    font-size: 0.9rem;
    font-weight: 600;
}

.bq-platform-overlay-options {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
}

.bq-platform-overlay-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 9rem;

    .bq-apikey-logo {
        width: 1.25rem;
        height: 1.25rem;
    }


}

.bq-input {
    flex: 1;
    min-height: var(--chat-control-height);
    box-sizing: border-box;
    overflow-y: hidden;
    resize: vertical;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    padding: 0.7rem 0.7rem 0.7rem var(--chat-control-height);
    font-family: @mono;
    font-size: 0.83rem;

    &::placeholder { color: @placeholder; }
    &:focus { outline: none; border-color: @ink; }
    &:disabled { opacity: 0.5; cursor: default; }
}

.btn {
    align-self: flex-end;
    min-width: 5.5rem;
    height: var(--chat-control-height);
}

// Local overrides so the API-key modal in this scoped file matches the one
// in service.vue (where `.bq-modal-textarea` and the modal `.btn` rule are
// scoped to that component and don't bleed into us).
.bq-modal-wrap {
    .bq-modal-textarea {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid @line;
        background: @paper;
        color: @ink;
        font-family: @mono;
        font-size: 0.9rem;
        outline: none;
        margin-bottom: 0.75rem;
        border-radius: 0;
        transition: border-color 0.15s;
        resize: vertical;
        min-height: 4rem;
        word-wrap: break-word;
        overflow-x: hidden;
        white-space: pre-wrap;

        &::placeholder { color: @placeholder; }
        &:focus { border-color: @ink; }
        &:disabled { opacity: 0.5; cursor: default; }
    }

    .btn {
        width: 100%;
        align-self: stretch;
        min-width: 0;
        height: auto;
    }
}

@media (max-width: 700px) {
    .bq-agent {
        height: var(--agent-available-height, calc(100dvh - 4rem));
    }

    .bq-meta {
        height: 100%;
        padding: 1rem 0;
        border: none;
        box-shadow: none;
    }

    .bq-input-row {
        flex-direction: column;
    }

    .bq-input-wrap {
        width: 100%;
        flex: 0 0 auto;
    }

    .bq-attachments {
        flex-basis: auto;
        width: 100%;
    }

    .bq-attachment-warning {
        flex-basis: auto;
        width: 100%;
    }

    .btn {
        width: 100%;
    }

    .bq-bubble {
        max-width: 100%;
    }
}
</style>
